import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from "react";
import { usePagination, useSortBy, useTable } from "react-table";
import moment from "moment";
import momentTimezone from "moment-timezone";
import {
  X,
  Pencil,
  ChevronDown,
  ChevronRight,
  Trash2,
  RotateCcw,
  Info,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
} from "lucide-react";
import { Tooltip } from "antd";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Loader from "../../../../reusableUI/loader";
import Page from "../../../../reusableUI/page";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import {
  getAssignedStaffList,
  actingHCPChange,
  getAvailableDutyHCPs,
  timeSlotChange,
  getNurseReshecduleSlots,
  getPhysioReshecduleSlots,
  dateSlotChange,
  closeDuty,
  getPartner,
  failLabCollection,
} from "../../../../Service/partnerAPI";
import {
  getLabResultUrlForAdmin,
  uploadLabResult,
} from "../../../../Service/adminVisits";
import { CommonImages } from "../../../../reusableUI/images";
import LiveTrackingMap from "../../../../reusableUI/LiveTrackingMap";
import PatientHistory from "../partnerPortal/patientHistory";
import { showToast } from "../../../../reusableUI/toaster";
import { IoMdBed } from "react-icons/io";
import { MdOutlineFilterList } from "react-icons/md";
import { FaUserNurse } from "react-icons/fa6";
// import { getLookupData } from "../../../../Service/lookup";
import {
  downloadDetailedBookingsPdf,
  filterExportableBookings,
  getPartnerIdForExport,
} from "../../../../utils/bookingDetailedExport";
import { buildLiveTrackingData } from "../../../../utils/liveTrackingVisit";

/* ---------------- Small helpers ---------------- */

const toDashTiming = (label = "") => String(label || "").replace(" to ", " - ");

const SortIcon = ({ isSorted, isSortedDesc }) => {
  if (!isSorted) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-gray-400"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 12l5 5 5-5H5zm10-4L10 3 5 8h10z" />
      </svg>
    );
  }
  if (isSortedDesc) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-blue-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5 5 5-5H5z" />
      </svg>
    );
  }
  return (
    <svg
      className="h-3 w-3 ml-1 text-blue-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M15 12l-5-5-5 5h10z" />
    </svg>
  );
};

const normalizeId = (id) => {
  if (!id) return "";
  if (typeof id === "string") return id;
  if (typeof id === "object" && id.$oid) return String(id.$oid);
  return String(id);
};

const isUpcomingDate = (dateStr) => {
  if (!dateStr) return false;
  return moment(dateStr).startOf("day").isSameOrAfter(moment().startOf("day"));
};

const isTodayDate = (dateStr) => {
  if (!dateStr) return false;
  return moment(dateStr).isSame(moment(), "day");
};

const hasTodayDuty = (visit) => {
  const dutyInfo = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  return dutyInfo.some((duty) => isTodayDate(duty?.date));
};

const isTrackingVisibleForDuty = (duty) => {
  return (
    moment(duty?.date).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD") &&
    String(duty?.liveStatus || "").toLowerCase() === "arriving"
  );
};

const formatFileSize = (size) => {
  const bytes = Number(size || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const LabResultPreviewModal = ({
  preview,
  isUploading,
  onChangeFile,
  onClose,
  onUpload,
}) => {
  if (!preview) return null;
  const fileSizeLabel = formatFileSize(preview.file.size);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-3 md:p-5"
      onClick={isUploading ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-result-preview-title"
    >
      <div
        className="flex h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 md:px-5">
          <div className="min-w-0">
            <h3
              id="lab-result-preview-title"
              className="text-[15px] font-semibold font-poppins text-gray-900"
            >
              Preview Lab Result
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-gray-500 font-poppins">
              {preview.file.name}
              {fileSizeLabel ? ` • ${fileSizeLabel}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close lab result preview"
            title="Close"
          >
            <X size={18} className="text-gray-700" />
          </button>
        </div>

        <div className="min-h-0 flex-1 bg-gray-100 p-2 md:p-4">
          <iframe
            src={`${preview.previewUrl}#toolbar=0&navpanes=0`}
            className="h-full w-full rounded-lg border-0 bg-white"
            title={`Preview of ${preview.file.name}`}
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-4 py-3 md:px-5">
          <button
            type="button"
            onClick={onChangeFile}
            disabled={isUploading}
            className="rounded-lg border border-[#4951EC] px-4 py-2 text-[12px] font-medium font-poppins text-[#4951EC] transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Change PDF
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="rounded-lg bg-gray-100 px-4 py-2 text-[12px] font-medium font-poppins text-gray-700 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onUpload}
            disabled={isUploading}
            className="rounded-lg bg-[#4951EC] px-4 py-2 text-[12px] font-medium font-poppins text-white transition hover:bg-[#3d3acc] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isUploading ? "Uploading..." : "Upload Result"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- ✅ REQUESTS FIX helpers ---------------- */

const isRequestsVisit = (visit) => {
  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();
  return st === "pending" || st === "payment pending" || st === "not paid";
};

const formatCompactDateRange = (startRaw, endRaw) => {
  const start = startRaw ? moment(startRaw) : null;
  const end = endRaw ? moment(endRaw) : null;
  const hasStart = start?.isValid();
  const hasEnd = end?.isValid();

  if (hasStart && hasEnd) {
    if (start.isSame(end, "day")) return start.format("D MMMM YYYY");
    if (start.isSame(end, "month")) {
      return `${start.format("D")}–${end.format("D MMMM YYYY")}`;
    }
    if (start.isSame(end, "year")) {
      return `${start.format("D MMMM")}–${end.format("D MMMM YYYY")}`;
    }
    return `${start.format("D MMMM YYYY")}–${end.format("D MMMM YYYY")}`;
  }

  if (hasStart) return start.format("D MMMM YYYY");
  if (hasEnd) return end.format("D MMMM YYYY");
  return "-";
};

const formatServiceDateRange = (visitInfo) => {
  if (!visitInfo) return "-";
  const startRaw = visitInfo?.startDate?.$date || visitInfo?.startDate;
  const endRaw = visitInfo?.endDate?.$date || visitInfo?.endDate;
  return formatCompactDateRange(startRaw, endRaw);
};

const getDutyStaffNameFromVisit = (visit) =>
  visit?.dutyStaff?.name ||
  visit?.specialistInfo?.name ||
  visit?.assessmentStaff?.name ||
  "N/A";

const getStandby1NameFromVisit = (visit) =>
  visit?.standBySpecialist?.name ||
  visit?.standBySpecialists?.specialists1Name ||
  "N/A";

const getStandby2NameFromVisit = (visit) =>
  visit?.backupSpecialist?.name ||
  visit?.standBySpecialists?.specialists2Name ||
  "N/A";

/**
 * Build per-day "virtual duties" when dutyInfo is empty (Requests).
 * Used for duty table sorting + reports modal so UI doesn't break.
 */
const buildVirtualDutiesFromVisitInfo = (visit) => {
  const startRaw = visit?.visitInfo?.startDate;
  const slot = String(visit?.visitInfo?.slot || "").trim();
  if (!startRaw || !slot) return [];

  const start = moment(startRaw).local().startOf("day");
  if (!start.isValid()) return [];

  const end = visit?.visitInfo?.endDate
    ? moment(visit.visitInfo.endDate).local().startOf("day")
    : null;

  const daysRaw = Number(visit?.visitInfo?.days);
  let totalDays =
    Number.isFinite(daysRaw) && daysRaw > 0 ? Math.floor(daysRaw) : 0;
  if (!totalDays && end?.isValid()) totalDays = end.diff(start, "days") + 1;
  if (!totalDays || totalDays <= 0) totalDays = 1;

  const staffName = getDutyStaffNameFromVisit(visit);
  const rows = [];
  for (let i = 0; i < totalDays; i++) {
    const d = start.clone().add(i, "day");
    if (end?.isValid() && d.isAfter(end, "day")) break;

    rows.push({
      _id: `virtual-${normalizeId(visit?._id) || "visit"}-${d.format("YYYY-MM-DD")}`,
      date: d.format("YYYY-MM-DD"),
      day: i + 1,
      slot,
      assignedStaffName: staffName,
      attendedBy: {
        role: visit?.specialistInfo?.role,
        userID: {
          $oid: normalizeId(
            visit?.dutyStaff?.userID || visit?.specialistInfo?.userID,
          ),
        },
      },
      startDuty: {},
      endDuty: {},
      liveStatus: "",
      payout: null,
      services: [],
    });
  }
  return rows;
};

const getDisplayDuties = (visit) => {
  if (!visit) return [];
  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();
  const dutyInfo = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];

  if (st === "payment pending" || st === "not paid") {
    const startRaw =
      visit?.visitInfo?.startDate?.$date || visit?.visitInfo?.startDate;
    const endRaw =
      visit?.visitInfo?.endDate?.$date || visit?.visitInfo?.endDate;
    const slot = String(visit?.visitInfo?.slot || "").trim();

    const dateStr = formatCompactDateRange(startRaw, endRaw);

    const staffName = getDutyStaffNameFromVisit(visit);
    return [
      {
        _id: `virtual-${normalizeId(visit?._id) || "visit"}-range`,
        date: startRaw || endRaw || null,
        displayDateRange: dateStr,
        slot,
        assignedStaffName: staffName,
        attendedBy: {
          role: visit?.specialistInfo?.role,
          userID: {
            $oid: normalizeId(
              visit?.dutyStaff?.userID || visit?.specialistInfo?.userID,
            ),
          },
        },
        startDuty: {},
        endDuty: {},
        liveStatus: "",
        payout: null,
        services: [],
      },
    ];
  }

  if (dutyInfo.length > 0) return dutyInfo;
  if (isRequestsVisit(visit)) return buildVirtualDutiesFromVisitInfo(visit);
  return [];
};

// Lab bookings without a duty are handled from Bookings until a collector is
// assigned. Keep the Lab badge in sync with the Visits table, which only
// renders records that produce at least one duty row, without changing the
// existing Homecare count behavior.
const hasRenderableVisitRows = (visit) => getDisplayDuties(visit).length > 0;

/* ---------------- Assessment / Treatment helpers ---------------- */

const getAssessments = (visit) =>
  Array.isArray(visit?.assessment) ? visit.assessment : [];

const getActiveAssessment = (visit) => {
  const arr = getAssessments(visit);
  return arr.find((a) => a?.active) || arr[0] || null;
};

// ✅ FIXED (your old expression returned "true"/"")
const getTreatmentPlanStatus = (visit) => {
  const assessment = getActiveAssessment(visit);
  return String(assessment?.treatmentPlan?.status || "")
    .trim()
    .toLowerCase();
};

const hasFurtherTreatmentRequested = (visit) => {
  const tpStatus = getTreatmentPlanStatus(visit);
  return tpStatus === "pending" || tpStatus === "requested";
};

const getFurtherTreatmentLabel = (visit) =>
  hasFurtherTreatmentRequested(visit) ? "Further Treatment Requested" : "";

/* ---------------- Visit status ---------------- */

const getVisitStatusLabel = (visit) => {
  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();

  if (hasFurtherTreatmentRequested(visit) && visit.dutyInfo.length > 0)
    return "Ongoing";
  if (
    hasFurtherTreatmentRequested(visit) &&
    visit.dutyInfo.length === 0 &&
    visit.status !== "completed" &&
    visit.status !== "expired" &&
    visit.status !== "not paid"
  )
    return "Payment pending";

  if (st === "accepted") return "Yet to Start";
  if (st === "startduty") return "Ongoing";
  if (st === "endduty") return "Ongoing";
  if (st === "completed") return "Completed";
  if (st === "rejected") return "Cancelled";
  if (st === "pending") return "Unassigned";
  if (st === "payment pending") return "Payment pending";
  if (st === "cancelled") return "Cancelled";
  if (st === "not paid") return "Voided";
  if (st === "not available") return "Not available";
  if (st === "expired") return "Expired";

  if (st.includes("startduty")) return "Ongoing";
  if (st.includes("endduty")) return "Completed";

  return st;
};

const StatusBadge = ({ status }) => {
  const normalizedStatus = String(status || "").trim();
  let colorClass = "text-red-500";

  if (
    normalizedStatus.includes("Yet to Start") ||
    normalizedStatus.includes("Accepted")
  )
    colorClass = "text-orange-600";
  else if (
    normalizedStatus.includes("Duty Started") ||
    normalizedStatus.includes("Ongoing")
  )
    colorClass = "text-blue-600";
  else if (
    normalizedStatus.includes("End Duty") ||
    normalizedStatus.includes("Completed")
  )
    colorClass = "text-teal-600";
  else if (
    normalizedStatus.includes("Cancelled") ||
    normalizedStatus.includes("Voided")
  )
    colorClass = "text-red-700";
  else if (normalizedStatus.includes("Unassigned")) colorClass = "text-red-600";
  else if (
    normalizedStatus.includes("Requested") ||
    normalizedStatus.includes("Payment pending")
  )
    colorClass = "text-orange-600";
  else if (
    normalizedStatus.includes("Not available") ||
    normalizedStatus.includes("Expired")
  )
    colorClass = "text-gray-500";

  const isPaymentPending = normalizedStatus.toLowerCase() === "payment pending";
  const isOngoingDash = normalizedStatus.toLowerCase().startsWith("ongoing -");

  return (
    <span
      className={`inline-flex items-center justify-start px-3 py-1.5 text-[13px] font-medium font-poppins rounded-full ${colorClass}`}
    >
      {isPaymentPending ? (
        <span className="flex flex-col items-start leading-snug text-left">
          <span>Payment</span>
          <span>Pending</span>
        </span>
      ) : isOngoingDash ? (
        <span className="flex flex-col items-start leading-snug max-w-[140px] whitespace-normal text-left">
          <span>{status.split("-")[0].trim()} -</span>
          <span>{status.substring(status.indexOf("-") + 1).trim()}</span>
        </span>
      ) : (
        status
      )}
    </span>
  );
};

/* ---------------- Duty status ---------------- */

const isDutyStarted = (duty) => !!duty?.startDuty?.verified;
const isDutyEnded = (duty) => !!duty?.endDuty?.verified;

const getDutyStatusLabel = (visit, duty) => {
  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();
  if (st === "not paid") return "Voided";
  if (st === "payment pending") return "Payment pending";

  const further = getFurtherTreatmentLabel(visit);

  if (st === "completed" || st.includes("completed")) {
    return further ? `Completed - ${further}` : "Completed";
  }

  if (!isDutyStarted(duty) && !isDutyEnded(duty)) return "Yet to Start";

  if (isDutyStarted(duty) && !isDutyEnded(duty)) {
    return further ? `Ongoing - ${further}` : "Ongoing";
  }

  if (isDutyEnded(duty)) {
    return further ? `End Duty - ${further}` : "End Duty";
  }

  return "Yet to Start";
};

const formatCheckTime = (t) => (t ? moment(t).format("hh:mm A") : "-");

const getDutyScheduledStartTime = (visit, duty) => {
  const st = duty?.startTime?.$date || duty?.startTime;
  if (st) return moment(st);

  const slot = duty?.slot || visit?.visitInfo?.slot;
  if (slot) {
    const { startTimeStr } = parseSelectedSlot(slot);
    if (startTimeStr && duty?.date) {
      const dateStr = moment(duty.date).format("YYYY-MM-DD");
      const combined = moment(
        `${dateStr} ${startTimeStr}`,
        "YYYY-MM-DD hh:mm A",
      );
      if (combined.isValid()) return combined;
    }
  }
  // return null;
};

const canEditDutyTime = (visit, duty) => {
  if (!visit || !duty || duty.displayDateRange) return false;

  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();
  const isAllowedStatus =
    st === "accepted" ||
    st === "startduty" ||
    st === "endduty" ||
    st === "payment pending";
  if (!isAllowedStatus) return false;

  if (isDutyStarted(duty) || isDutyEnded(duty)) return false;

  return isUpcomingDate(duty?.date);
};

const canEditDutyDateAndStaff = (visit, duty) => {
  if (!visit || !duty || duty.displayDateRange) return false;

  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();
  const isAllowedStatus =
    st === "accepted" ||
    st === "startduty" ||
    st === "endduty" ||
    st === "payment pending";
  if (!isAllowedStatus) return false;

  if (isDutyStarted(duty) || isDutyEnded(duty)) return false;

  return isUpcomingDate(duty?.date);
};

const canCancelDuty = (visit, duty) => {
  if (!visit || !duty || duty.displayDateRange) return false;

  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();
  const isAllowedStatus =
    st === "accepted" ||
    st === "startduty" ||
    st === "endduty" ||
    st === "payment pending";
  if (!isAllowedStatus) return false;

  if (isDutyStarted(duty) || isDutyEnded(duty)) return false;

  if (!isUpcomingDate(duty?.date)) return false;

  if (isTodayDate(duty?.date)) {
    const scheduledTime = getDutyScheduledStartTime(visit, duty);
    if (scheduledTime && scheduledTime.isValid()) {
      const diffHours = scheduledTime.diff(moment(), "hours", true);
      return diffHours >= 3;
    }
    return false;
  }

  return true;
};

/**
 * ✅ Robust assigned staff resolver (includes virtual assignedStaffName)
 */
const getDutyHcpName = (visit, duty) => {
  if (duty?.payouts?.attendedBy) {
    return duty.payouts.attendedBy;
  }

  const nameInfo = duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo;
  if (nameInfo?.fullName) {
    return `${nameInfo?.title ? nameInfo.title + " " : ""}${nameInfo.fullName}`.trim();
  }

  if (typeof duty?.attendedBy === "string" && duty.attendedBy.trim()) {
    return duty.attendedBy.trim();
  }
  if (duty?.attendedBy?.name) {
    return duty.attendedBy.name;
  }

  const dutyLevelName =
    duty?.specialistInfo?.name ||
    duty?.dutyStaff?.name ||
    duty?.actingHCP?.name ||
    duty?.actingHcpInfo?.name ||
    duty?.assignedStaff?.name ||
    duty?.assignedStaffName;

  if (dutyLevelName) return dutyLevelName;

  return visit?.dutyStaff?.name || visit?.specialistInfo?.name || "N/A";
};

const formatDutyTimeFromStartEnd = (duty, visit) => {
  const st = duty?.startTime?.$date || duty?.startTime;
  const et = duty?.endTime?.$date || duty?.endTime;
  const role = String(visit?.specialistInfo?.role || "")
    .trim()
    .toLowerCase();
  const isOneTime =
    role !== "physiotherapist" &&
    String(visit?.dutyType || "")
      .trim()
      .toLowerCase() === "onetime";

  if (!st && !et) return "";

  if (isOneTime) {
    const timeToUse = st || et;
    return timeToUse ? moment(timeToUse).local().format("hh:mm A") : "";
  }

  if (st && et) {
    const startLocal = moment(st).local().format("hh:mm A");
    const endLocal = moment(et).local().subtract(1, "hour").format("hh:mm A");
    if (startLocal === endLocal) {
      return startLocal;
    }
    return `${startLocal} to ${endLocal}`;
  }

  if (st && !et) {
    const startLocal = moment(st).local().format("hh:mm A");
    const slot = duty?.slot || visit?.visitInfo?.slot;
    if (slot && (slot.includes(" to ") || slot.includes(" - "))) {
      return slot;
    }
    return startLocal;
  }

  if (!st && et) {
    return moment(et).local().subtract(1, "hour").format("hh:mm A");
  }

  return "";
};

const getDutyTimeLabel = (visit, duty, dutySlotsOverride, dutyKey) => {
  const byStartEnd = formatDutyTimeFromStartEnd(duty, visit);
  if (byStartEnd) return byStartEnd;

  if (dutySlotsOverride?.[dutyKey]) return dutySlotsOverride[dutyKey];
  if (duty?.slot) return duty.slot;

  if (visit?.visitInfo?.slot) return visit.visitInfo.slot;

  const tpLabel = getActiveAssessment(visit)?.treatmentPlan?.timings?.label;
  if (tpLabel) return tpLabel;

  return "-";
};

/* ---------------- Payout helpers ---------------- */

const formatAmount = (n) => {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
    num,
  );
};

const getPayoutText = (duty) => {
  const amount =
    duty?.payout?.payoutToTheVendor?.amountToTransfer ??
    duty?.payout?.fees ??
    null;
  const status = duty?.payout?.payoutToTheVendor?.status
    ? String(duty.payout.payoutToTheVendor.status).toLowerCase()
    : "";
  const amtStr = formatAmount(amount);

  if (!amtStr && !status) return "-";
  if (amtStr && status) return `${"Rs. " + amtStr} (${status})`;
  if (amtStr) return "Rs. " + amtStr;
  return `(${status})`;
};

const getDutyPayoutText = (visit, duty, started) => {
  const isDailyQr =
    String(visit?.paymentMode || "")
      .trim()
      .toLowerCase() === "daily_qr";
  const paymentStatus = String(duty?.payment?.status || "")
    .trim()
    .toLowerCase();

  if (isDailyQr && paymentStatus !== "paid") return "-";
  if (!started && !isDailyQr) return "-";

  const amount =
    duty?.payout?.payoutToTheVendor?.amountToTransfer ??
    duty?.payout?.payoutToTheVendor?.netPayout ??
    duty?.payout?.fees ??
    null;
  const hasPositiveAmount =
    Number.isFinite(Number(amount)) && Number(amount) > 0;
  const status = duty?.payout?.payoutToTheVendor?.status
    ? String(duty.payout.payoutToTheVendor.status).toLowerCase()
    : "";

  if (isDailyQr && !hasPositiveAmount) {
    return status ? `(${status})` : "-";
  }

  return getPayoutText(duty);
};

const getPayoutColorClass = (status) => {
  const st = String(status || "").toLowerCase();
  if (st === "processed" || st === "success" || st === "paid")
    return "text-green-700";
  if (st === "pending" || st === "initiated") return "text-orange-500";
  if (st === "failed" || st === "rejected") return "text-red-700";
  return "text-gray-700";
};

/* ---------------- Physio slots ---------------- */

const PHYSIO_TIME_SLOTS = [
  "06:00 AM to 07:00 AM",
  "07:00 AM to 08:00 AM",
  "08:00 AM to 09:00 AM",
  "09:00 AM to 10:00 AM",
  "10:00 AM to 11:00 AM",
  "11:00 AM to 12:00 PM",
  "12:00 PM to 01:00 PM",
  "01:00 PM to 02:00 PM",
  "02:00 PM to 03:00 PM",
  "03:00 PM to 04:00 PM",
  "04:00 PM to 05:00 PM",
  "05:00 PM to 06:00 PM",
  "06:00 PM to 07:00 PM",
  "07:00 PM to 08:00 PM",
  "08:00 PM to 09:00 PM",
];

const parseSelectedSlot = (slot) => {
  if (!slot) return { startTimeStr: "", endTimeStr: "" };
  let parts = [];
  if (slot.includes(" to ")) {
    parts = slot.split(" to ");
  } else if (slot.includes(" - ")) {
    parts = slot.split(" - ");
  } else if (slot.includes("-")) {
    parts = slot.split("-");
  } else {
    parts = [slot];
  }
  const from = (parts[0] || "").trim();
  const to = (parts[1] || "").trim();
  return { startTimeStr: from, endTimeStr: to };
};

const pickInitialSlotForModal = (currentSlot, visitSlot) => {
  if (PHYSIO_TIME_SLOTS.includes(currentSlot)) return currentSlot;
  if (PHYSIO_TIME_SLOTS.includes(visitSlot)) return visitSlot;
  return PHYSIO_TIME_SLOTS[0];
};

/* ---------------- Report UI helpers (unchanged) ---------------- */

const isEncryptedString = (v) =>
  typeof v === "string" && v.startsWith("enc:v1:");
const truncate = (str, n = 42) => {
  const s = String(str ?? "");
  if (s.length <= n) return s;
  return s.slice(0, n) + "…";
};
const maybeDecrypt = (v) => v;

const formatValue = (val) => {
  if (val == null) return "-";
  if (typeof val === "boolean") return val ? "Yes" : "No";

  if (Array.isArray(val)) {
    if (!val.length) return "-";
    return val
      .map(maybeDecrypt)
      .map((x) => (isEncryptedString(x) ? truncate(x, 36) : String(x)))
      .join(", ");
  }

  if (typeof val === "object") {
    const entries = Object.entries(val || {});
    if (!entries.length) return "-";
    return entries.map(([k, v]) => `${k}: ${formatValue(v)}`).join("\n");
  }

  const v = maybeDecrypt(val);
  if (isEncryptedString(v)) return truncate(v, 36);

  const s = String(v).trim();
  return s ? s : "-";
};

const pickFromValues = (values, keys) => {
  const obj = values && typeof values === "object" ? values : {};
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
};

const getServiceProvided = (values) =>
  pickFromValues(values, [
    "servicesProvided",
    "serviceProvided",
    "Services Provided",
    "Service Provided",
  ]);
const getImprovements = (values) =>
  pickFromValues(values, ["improvements", "Improvement", "Improvements"]);
const getServiceProvidedDetails = (values) =>
  pickFromValues(values, [
    "serviceProvidedDetails",
    "providedDetails",
    "Service Provided Details",
  ]);
const getServiceNameField = (values) =>
  pickFromValues(values, ["serviceName", "Service Name"]);
const getNote = (values, srv) =>
  pickFromValues(values, ["notes", "note", "Note", "Notes"]) ||
  srv?.notes ||
  srv?.note ||
  "";

const PAIN_SCALE_LABELS = [
  { value: 0, label: "No Pain" },
  { value: 1, label: "Mild" },
  { value: 2, label: "Mild" },
  { value: 3, label: "Mild" },
  { value: 4, label: "Moderate" },
  { value: 5, label: "Moderate" },
  { value: 6, label: "Severe" },
  { value: 7, label: "Severe" },
  { value: 8, label: "Very Severe" },
  { value: 9, label: "Very Severe" },
  { value: 10, label: "Worst Pain Possible" },
];

const SERVICE_NAME_EXCLUDE_CHECKLIST = new Set([
  "back care",
  "vital signs",
  "nutrition",
  "suppository",
  "wound care",
  "continuous ryle's tube aspiration",
  "hourly ryle's tube aspiration",
  "additional service",
  "medication management",
]);

const asArray = (val) => (Array.isArray(val) ? val : []);
const asObject = (val) =>
  val && typeof val === "object" && !Array.isArray(val) ? val : {};
const hasText = (val) => String(val ?? "").trim().length > 0;

const getPainScaleText = (srv, rawValue) => {
  let n = NaN;
  if (srv?.value !== undefined && srv?.value !== null) {
    n = Number(srv.value);
  } else if (rawValue && typeof rawValue === "object") {
    n = Number(rawValue.painScale ?? rawValue.value);
  } else {
    n = Number(rawValue);
  }

  if (!Number.isFinite(n)) return "";
  const idx = Math.max(0, Math.min(10, Math.round(n)));
  const row = PAIN_SCALE_LABELS[idx];
  if (!row) return "";

  const severity =
    rawValue && typeof rawValue === "object" && rawValue.severity
      ? rawValue.severity
      : row.label;
  return `${row.value}/10 - ${severity}`;
};

const SERVICE_TAB_HOMECARE = "homecare";
const SERVICE_TAB_LAB = "lab";

const isLabServiceVisit = (visit) =>
  String(visit?.serviceCategory || "").trim().toLowerCase() === "lab";

const hasLabResultDocument = (visit) =>
  Boolean(
    visit?.labInfo?.reportDocument?.available === true ||
      String(visit?.labInfo?.reportDocument?.key || "").trim(),
  );

const hasSuccessfullyCollectedLabDuty = (visit) =>
  Array.isArray(visit?.dutyInfo) &&
  visit.dutyInfo.some((duty) => {
    const status = String(duty?.labCollection?.status || "")
      .trim()
      .toLowerCase();
    return status === "collected" && Boolean(duty?.labCollection?.completedAt);
  });

const isLabResultPendingVisit = (visit) => {
  const isLabVisit =
    String(visit?.serviceCategory || "").trim().toLowerCase() === "lab";
  const visitStatus = String(visit?.status || "")
    .trim()
    .toLowerCase();

  return (
    isLabVisit &&
    (visitStatus === "completed" || visitStatus.includes("completed")) &&
    hasSuccessfullyCollectedLabDuty(visit) &&
    !hasLabResultDocument(visit)
  );
};

const isSafeLabResultUrl = (url) => /^https?:\/\//i.test(String(url || "").trim());

const LAB_COLLECTION_STAGE_LABELS = Object.freeze({
  empty_tubes: "Empty tubes",
  sample_collection: "Sample collection",
  collected_samples: "Collected samples",
  packed_samples: "Packed samples",
});

const LAB_COLLECTION_STATUS_LABELS = Object.freeze({
  not_started: "Not started",
  in_progress: "In progress",
  collected: "Collected",
  failed: "Failed",
});

const LAB_COLLECTION_STAGE_ORDER = Object.freeze([
  "empty_tubes",
  "collected_samples",
  "packed_samples",
]);

const formatLabCollectionDateTime = (value) => {
  if (!value) return "-";
  const parsed = momentTimezone(value).tz("Asia/Kolkata");
  return parsed.isValid() ? parsed.format("DD/MM/YYYY hh:mm A") : "-";
};

const getLabCollectionStageLabel = (stage) => {
  const normalizedStage = String(stage || "").trim().toLowerCase();
  return (
    LAB_COLLECTION_STAGE_LABELS[normalizedStage] ||
    normalizedStage.replace(/_/g, " ") ||
    "Collection photo"
  );
};

const getCollectedSampleEvidenceNumbers = (evidenceList) =>
  new Set(
    evidenceList
      .filter(
        (item) =>
          String(item?.stage || "").trim().toLowerCase() ===
          "collected_samples",
      )
      .map((item) => Number(item?.sampleNumber))
      .filter((sampleNumber) => Number.isInteger(sampleNumber) && sampleNumber > 0),
  );

// Mirrors SampleCollection.js: the sample collection step is complete only
// after every declared sample has a photo and the phlebotomist has moved on
// from that step. Partial in-progress data must not reach the report view.
const isSampleCollectionStepCompleted = (labCollection, evidenceList) => {
  const rounds = asArray(labCollection?.rounds);
  if (rounds.length > 0) {
    return rounds.every((round) => {
      const roundEvidence = asArray(round?.evidence);
      const sampleCount = Number(round?.sampleCount);
      if (!Number.isInteger(sampleCount) || sampleCount < 1) return false;
      if (!round?.collectionTime || round?.status !== "collected") return false;
      const collectedSampleNumbers = getCollectedSampleEvidenceNumbers(
        roundEvidence,
      );
      return Array.from(
        { length: sampleCount },
        (_, index) => index + 1,
      ).every((sampleNumber) => collectedSampleNumbers.has(sampleNumber));
    });
  }

  const sampleCount = Number(labCollection?.sampleCount);
  if (!Number.isInteger(sampleCount) || sampleCount < 1) return false;
  if (!labCollection?.collectionTime) return false;

  const collectedSampleNumbers = getCollectedSampleEvidenceNumbers(evidenceList);
  for (let sampleNumber = 1; sampleNumber <= sampleCount; sampleNumber += 1) {
    if (!collectedSampleNumbers.has(sampleNumber)) return false;
  }

  const status = String(labCollection?.status || "").trim().toLowerCase();
  const currentStep = String(labCollection?.currentStep || "")
    .trim()
    .toLowerCase();

  return (
    currentStep === "packed_samples" ||
    ["collected", "failed"].includes(status)
  );
};

// New records store all three photo stages under their selected collection
// round. Keep the root evidence fallback for legacy single-round records.
const getLabCollectionEvidence = (labCollection) => {
  const allEvidence = [
    ...asArray(labCollection?.rounds).flatMap((round) =>
      asArray(round?.evidence).map((item) => ({
        ...item,
        collectionRoundKey: round?.key,
        collectionRoundLabel: round?.label,
        collectionRoundStatus: round?.status,
      })),
    ),
    ...asArray(labCollection?.evidence),
  ];
  const seen = new Set();

  return allEvidence.filter((item, index) => {
    const identity = String(
      item?.evidenceID || item?.key || `lab-evidence-${index}`,
    ).trim();
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

const LAB_PHOTO_VIEWER_MIN_ZOOM = 1;
const LAB_PHOTO_VIEWER_MAX_ZOOM = 4;
const LAB_PHOTO_VIEWER_ZOOM_STEP = 0.25;

const LabCollectionPhotoViewer = ({ photo, onClose }) => {
  const [scale, setScale] = useState(LAB_PHOTO_VIEWER_MIN_ZOOM);
  const [rotation, setRotation] = useState(0);
  const [naturalSize, setNaturalSize] = useState(null);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: 1024,
    height: 768,
  }));

  const url = String(photo?.url || "").trim();
  const safeUrl = /^https?:\/\//i.test(url) ? url : "";

  useEffect(() => {
    const updateViewportSize = () =>
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    updateViewportSize();
    window.addEventListener("resize", updateViewportSize);
    return () => window.removeEventListener("resize", updateViewportSize);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!safeUrl) return null;

  const stageLabel = getLabCollectionStageLabel(photo?.stage);
  const roundLabel = String(photo?.collectionRoundLabel || "").trim();
  const stageTitle = photo?.sampleNumber
    ? `Sample ${photo.sampleNumber}`
    : stageLabel;
  const photoTitle = roundLabel
    ? `${roundLabel} • ${stageTitle}`
    : stageTitle;

  const availableWidth = Math.max(240, Math.min(880, viewportSize.width - 96));
  const availableHeight = Math.max(240, viewportSize.height - 268);
  const fitRatio = naturalSize
    ? Math.min(
        availableWidth / naturalSize.width,
        availableHeight / naturalSize.height,
        1,
      )
    : 1;

  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const isSideways = normalizedRotation === 90 || normalizedRotation === 270;
  const frameStyle = naturalSize
    ? {
        width: Math.round(
          (isSideways ? naturalSize.height : naturalSize.width) *
            fitRatio *
            scale,
        ),
        height: Math.round(
          (isSideways ? naturalSize.width : naturalSize.height) *
            fitRatio *
            scale,
        ),
      }
    : undefined;

  const zoomOut = () =>
    setScale((current) =>
      Math.max(
        LAB_PHOTO_VIEWER_MIN_ZOOM,
        Math.round((current - LAB_PHOTO_VIEWER_ZOOM_STEP) * 100) / 100,
      ),
    );
  const zoomIn = () =>
    setScale((current) =>
      Math.min(
        LAB_PHOTO_VIEWER_MAX_ZOOM,
        Math.round((current + LAB_PHOTO_VIEWER_ZOOM_STEP) * 100) / 100,
      ),
    );
  const resetView = () => {
    setScale(LAB_PHOTO_VIEWER_MIN_ZOOM);
    setRotation(0);
  };

  const controlButtonClass =
    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`View ${photoTitle}`}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <h4 className="truncate text-[13px] font-semibold font-poppins text-gray-900">
              {photoTitle}
            </h4>
            <p className="mt-0.5 truncate text-[10px] text-gray-500 font-poppins">
              {photo?.sampleNumber ? `${stageLabel} • ` : ""}
              Captured: {formatLabCollectionDateTime(photo?.capturedAt)}
              {photo?.uploadedAt
                ? ` • Uploaded: ${formatLabCollectionDateTime(photo.uploadedAt)}`
                : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition hover:bg-gray-100"
            title="Close"
            aria-label="Close photo viewer"
          >
            <X size={18} className="text-gray-700" />
          </button>
        </div>

        <div className="flex min-h-[300px] flex-1 overflow-auto bg-gray-900 p-4">
          <div className="relative m-auto shrink-0" style={frameStyle}>
            <img
              src={safeUrl}
              alt={photoTitle}
              onLoad={(event) => {
                const image = event.currentTarget;
                if (image.naturalWidth && image.naturalHeight) {
                  setNaturalSize({
                    width: image.naturalWidth,
                    height: image.naturalHeight,
                  });
                }
              }}
              onDoubleClick={() => setScale((current) => (current > 1 ? 1 : 2))}
              style={{
                width: naturalSize
                  ? Math.round(naturalSize.width * fitRatio)
                  : undefined,
                height: naturalSize
                  ? Math.round(naturalSize.height * fitRatio)
                  : undefined,
                transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${scale})`,
              }}
              className={
                naturalSize
                  ? "absolute left-1/2 top-1/2 max-w-none select-none"
                  : "max-h-[60vh] max-w-full select-none object-contain"
              }
              draggable={false}
            />
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={zoomOut}
            className={controlButtonClass}
            disabled={scale <= LAB_PHOTO_VIEWER_MIN_ZOOM}
            title="Zoom out"
            aria-label="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="min-w-[3.5rem] text-center text-[11px] font-semibold font-poppins text-gray-700">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={zoomIn}
            className={controlButtonClass}
            disabled={scale >= LAB_PHOTO_VIEWER_MAX_ZOOM}
            title="Zoom in"
            aria-label="Zoom in"
          >
            <ZoomIn size={16} />
          </button>

          <span className="mx-1 h-6 w-px bg-gray-200" />

          <button
            type="button"
            onClick={() => setRotation((current) => current - 90)}
            className={controlButtonClass}
            title="Rotate left"
            aria-label="Rotate left"
          >
            <RotateCcw size={16} />
          </button>
          <button
            type="button"
            onClick={() => setRotation((current) => current + 90)}
            className={controlButtonClass}
            title="Rotate right"
            aria-label="Rotate right"
          >
            <RotateCw size={16} />
          </button>

          <span className="mx-1 h-6 w-px bg-gray-200" />

          <button
            type="button"
            onClick={resetView}
            className={controlButtonClass}
            disabled={scale === LAB_PHOTO_VIEWER_MIN_ZOOM && rotation === 0}
            title="Reset view"
            aria-label="Reset view"
          >
            <Maximize2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

const LabCollectionReport = ({ collection }) => {
  const labCollection = asObject(collection);
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const evidence = getLabCollectionEvidence(labCollection).sort((left, right) => {
    const leftStage = LAB_COLLECTION_STAGE_ORDER.indexOf(
      String(left?.stage || "").trim().toLowerCase(),
    );
    const rightStage = LAB_COLLECTION_STAGE_ORDER.indexOf(
      String(right?.stage || "").trim().toLowerCase(),
    );
    if (leftStage !== rightStage) return leftStage - rightStage;

    return (
      moment(left?.capturedAt || left?.uploadedAt || 0).valueOf() -
      moment(right?.capturedAt || right?.uploadedAt || 0).valueOf()
    );
  });

  const statusKey = String(labCollection.status || "not_started")
    .trim()
    .toLowerCase();
  const statusLabel =
    LAB_COLLECTION_STATUS_LABELS[statusKey] ||
    statusKey.replace(/_/g, " ") ||
    "Not started";
  const currentStep = String(labCollection.currentStep || "")
    .trim()
    .toLowerCase();
  const isSampleStepCompleted = isSampleCollectionStepCompleted(
    labCollection,
    evidence,
  );
  const hasWithheldSampleEvidence = evidence.some(
    (item) =>
      String(item?.stage || "").trim().toLowerCase() === "collected_samples",
  );
  const completedRounds = asArray(labCollection?.rounds).filter(
    (round) => round?.status === "collected",
  );
  const completedSampleCount = completedRounds.reduce(
    (total, round) => total + (Number(round?.sampleCount) || 0),
    0,
  );
  const completedRoundCollectionTimes = completedRounds
    .map((round) => round?.collectionTime)
    .filter(Boolean)
    .sort(
      (left, right) => moment(left).valueOf() - moment(right).valueOf(),
    );
  const collectionTimeDisplay = labCollection.collectionTime
    ? formatLabCollectionDateTime(labCollection.collectionTime)
    : completedRoundCollectionTimes.length === 1
      ? formatLabCollectionDateTime(completedRoundCollectionTimes[0])
      : completedRoundCollectionTimes.length > 1
        ? `${formatLabCollectionDateTime(
            completedRoundCollectionTimes[0],
          )} - ${formatLabCollectionDateTime(
            completedRoundCollectionTimes[completedRoundCollectionTimes.length - 1],
          )}`
        : "-";
  const visibleEvidence = isSampleStepCompleted
    ? evidence
    : evidence.filter(
        (item) =>
          String(item?.stage || "").trim().toLowerCase() !==
            "collected_samples" || item?.collectionRoundStatus === "collected",
      );
  const collectionRounds = asArray(labCollection?.rounds);
  const roundKeys = new Set(
    collectionRounds.map((round) => String(round?.key ?? "").trim()),
  );
  const evidenceByRoundKey = new Map();
  const ungroupedEvidence = [];
  visibleEvidence.forEach((item) => {
    const roundKey = String(item?.collectionRoundKey ?? "").trim();
    if (roundKey && roundKeys.has(roundKey)) {
      const bucket = evidenceByRoundKey.get(roundKey) ?? [];
      bucket.push(item);
      evidenceByRoundKey.set(roundKey, bucket);
      return;
    }
    ungroupedEvidence.push(item);
  });
  const hasRoundGroups = roundKeys.size > 0;

  const getRoundLabel = (round) => {
    const label = String(round?.label || "").trim();
    if (label) return label;
    const fallback = String(round?.key || "")
      .trim()
      .replace(/_/g, " ");
    return fallback
      ? fallback.charAt(0).toUpperCase() + fallback.slice(1)
      : "Collection round";
  };

  const getRoundStatusLabel = (round) => {
    const status = String(round?.status || "not_started")
      .trim()
      .toLowerCase();
    return (
      LAB_COLLECTION_STATUS_LABELS[status] ||
      status.replace(/_/g, " ") ||
      "Not started"
    );
  };

  const renderEvidenceCard = (item, index, roundLabel = "") => {
    const evidenceUrl = String(item?.url || "").trim();
    const safeEvidenceUrl = /^https?:\/\//i.test(evidenceUrl)
      ? evidenceUrl
      : "";
    const stageLabel = getLabCollectionStageLabel(item?.stage);
    const photoLabel = item?.sampleNumber
      ? `${stageLabel} • Sample ${item.sampleNumber}`
      : stageLabel;
    const displayPhotoLabel = roundLabel
      ? `${roundLabel} • ${photoLabel}`
      : photoLabel;

    return (
      <div
        key={
          item?.evidenceID ||
          item?.key ||
          `${displayPhotoLabel}-${index}`
        }
        className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50"
      >
        {safeEvidenceUrl ? (
          <button
            type="button"
            onClick={() => setViewerPhoto(item)}
            className="block w-full cursor-zoom-in bg-gray-100 transition hover:opacity-90"
            aria-label={`View ${displayPhotoLabel}`}
          >
            <img
              src={safeEvidenceUrl}
              alt={displayPhotoLabel}
              loading="lazy"
              className="h-40 w-full object-cover"
            />
          </button>
        ) : (
          <div className="h-40 flex items-center justify-center px-3 text-center text-[10px] text-gray-500 font-poppins">
            Photo link unavailable
          </div>
        )}
        <div className="p-3 text-[10px] font-poppins">
          <div className="font-semibold text-gray-800">{photoLabel}</div>
          <div className="mt-1 text-gray-500">
            Captured: {formatLabCollectionDateTime(item?.capturedAt)}
          </div>
          {item?.uploadedAt && (
            <div className="mt-0.5 text-gray-500">
              Uploaded: {formatLabCollectionDateTime(item.uploadedAt)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEvidenceGrid = (items, roundLabel = "") => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((item, index) => renderEvidenceCard(item, index, roundLabel))}
    </div>
  );

  return (
    <div className="rounded-xl border-2 border-emerald-200 overflow-hidden bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-emerald-50 px-4 py-3 border-b border-emerald-100">
        <div>
          <h4 className="text-[13px] font-bold font-poppins text-emerald-950">
            Lab Collection
          </h4>
          <p className="text-[10px] text-emerald-700 font-poppins mt-0.5">
            Phlebotomist collection data and evidence
          </p>
        </div>
        <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold font-poppins text-emerald-800 border border-emerald-200">
          {statusLabel}
        </span>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-[11px] font-poppins">
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <span className="block text-gray-500 font-medium">Current step</span>
            <span className="block mt-1 text-gray-900 font-semibold">
              {currentStep ? getLabCollectionStageLabel(currentStep) : "-"}
            </span>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <span className="block text-gray-500 font-medium">Samples collected</span>
            <span className="block mt-1 text-gray-900 font-semibold">
              {completedSampleCount > 0
                ? completedSampleCount
                : isSampleStepCompleted
                  ? (labCollection.sampleCount ?? "-")
                  : "-"}
            </span>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <span className="block text-gray-500 font-medium">Collection time</span>
            <span className="block mt-1 text-gray-900 font-semibold">
              {isSampleStepCompleted ? collectionTimeDisplay : "-"}
            </span>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <span className="block text-gray-500 font-medium">Completed at</span>
            <span className="block mt-1 text-gray-900 font-semibold">
              {formatLabCollectionDateTime(labCollection.completedAt)}
            </span>
          </div>
        </div>

        {hasText(labCollection.notes) && (
          <div className="rounded-lg border border-gray-200 p-3 text-[11px] font-poppins">
            <span className="block text-gray-500 font-medium">Collection notes</span>
            <span className="block mt-1 text-gray-800 whitespace-pre-line break-words">
              {labCollection.notes}
            </span>
          </div>
        )}

        {hasText(labCollection.failureReason) && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[11px] font-poppins">
            <span className="block text-red-700 font-semibold">Failure reason</span>
            <span className="block mt-1 text-red-900 whitespace-pre-line break-words">
              {labCollection.failureReason}
            </span>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <h5 className="text-[12px] font-bold font-poppins text-gray-900">
              Collection photos
            </h5>
            <span className="text-[10px] text-gray-500 font-poppins">
              {visibleEvidence.length}{" "}
              {visibleEvidence.length === 1 ? "photo" : "photos"}
            </span>
          </div>

          {!isSampleStepCompleted && hasWithheldSampleEvidence && (
            <p className="mb-2 text-[10px] text-gray-500 font-poppins">
              Collected sample details appear once the phlebotomist completes
              the sample collection step.
            </p>
          )}

          {visibleEvidence.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center text-[11px] text-gray-500 font-poppins">
              No collection photos recorded.
            </div>
          ) : hasRoundGroups ? (
            <div className="space-y-4">
              {collectionRounds.map((round, roundIndex) => {
                const roundKey = String(round?.key ?? "").trim();
                const roundLabel = getRoundLabel(round);
                const roundEvidence = evidenceByRoundKey.get(roundKey) ?? [];
                const roundStatusKey = String(round?.status || "not_started")
                  .trim()
                  .toLowerCase();
                const roundCollected = roundStatusKey === "collected";

                return (
                  <div
                    key={
                      round?._id
                        ? normalizeId(round._id)
                        : `round-${roundKey || roundIndex}`
                    }
                    className="rounded-lg border border-gray-200 overflow-hidden bg-white"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[12px] font-bold font-poppins text-gray-900">
                          {roundLabel}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[9px] font-semibold font-poppins border ${
                            roundCollected
                              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : "bg-white text-gray-600 border-gray-200"
                          }`}
                        >
                          {getRoundStatusLabel(round)}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-500 font-poppins">
                        {roundEvidence.length}{" "}
                        {roundEvidence.length === 1 ? "photo" : "photos"}
                        {Number.isInteger(Number(round?.sampleCount)) &&
                        Number(round?.sampleCount) > 0
                          ? ` • Samples: ${Number(round.sampleCount)}`
                          : ""}
                        {round?.collectionTime
                          ? ` • Collected: ${formatLabCollectionDateTime(round.collectionTime)}`
                          : ""}
                      </span>
                    </div>
                    <div className="p-3">
                      {roundEvidence.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-3 text-center text-[10px] text-gray-500 font-poppins">
                          No photos recorded for this round yet.
                        </div>
                      ) : (
                        renderEvidenceGrid(roundEvidence, roundLabel)
                      )}
                    </div>
                  </div>
                );
              })}

              {ungroupedEvidence.length > 0 && (
                <div>
                  <div className="mb-2 text-[11px] font-semibold font-poppins text-gray-700">
                    Other collection photos
                  </div>
                  {renderEvidenceGrid(ungroupedEvidence)}
                </div>
              )}
            </div>
          ) : (
            renderEvidenceGrid(visibleEvidence)
          )}
        </div>
      </div>

      {viewerPhoto && (
        <LabCollectionPhotoViewer
          photo={viewerPhoto}
          onClose={() => setViewerPhoto(null)}
        />
      )}
    </div>
  );
};

/* ---------------- Filter helper ---------------- */

const getVisitBucket = (visit) => {
  const st = String(visit?.status || "")
    .trim()
    .toLowerCase();

  // ✅ Hard exits first — these must never fall into Live
  if (st === "expired") return "Others";
  if (st === "cancelled") return "Others";
  if (st === "not paid") return "Others";
  if (st === "not available") return "Others";
  if (st === "rejected") return "Others";

  // ── Requests ──────────────────────────────────────────────────────────────
  if (st === "pending" || st === "payment pending") return "Payment Pending";

  // ── Live ──────────────────────────────────────────────────────────────────
  if (hasFurtherTreatmentRequested(visit)) return "Live";
  if (hasTodayDuty(visit)) return "Live";
  if (st === "startduty") return "Live";
  if (st.includes("startduty")) return "Live";

  // ── Completed ─────────────────────────────────────────────────────────────
  if (st === "completed") return "Completed";
  if (st === "endduty") return "Completed";
  if (st.includes("endduty")) return "Completed";

  // ── Upcoming ──────────────────────────────────────────────────────────────
  if (st === "accepted") return "Upcoming";

  return "Upcoming";
};

const matchesFilter = (visit, duty, filter) => {
  const isStarted = !!duty?.startDuty?.verified;
  const isEnded = !!duty?.endDuty?.verified;
  const isToday = duty?.date
    ? moment(duty.date).isSame(moment(), "day")
    : false;
  const visitStatus = String(visit?.status || "")
    .trim()
    .toLowerCase();

  if (visitStatus === "pending") {
    return false;
  }

  if (filter === "All") {
    if (
      [
        "expired",
        "cancelled",
        "not paid",
        "not available",
        "rejected",
      ].includes(visitStatus)
    ) {
      return false;
    }
    return (
      matchesFilter(visit, duty, "Ongoing") ||
      matchesFilter(visit, duty, "Upcoming") ||
      matchesFilter(visit, duty, "Payment Pending") ||
      matchesFilter(visit, duty, "Action Required")
    );
  }
  if (filter === "Upcoming") {
    if (
      [
        "expired",
        "cancelled",
        "not paid",
        "not available",
        "rejected",
      ].includes(visitStatus)
    ) {
      return false;
    }
    const isVisitOngoing = ["ongoing", "startduty", "endduty"].some((s) =>
      visitStatus.includes(s),
    );
    return (
      !isStarted &&
      !isEnded &&
      (!isToday || visitStatus === "accepted" || isVisitOngoing)
    );
  }
  if (filter === "Ongoing") {
    if (
      [
        "expired",
        "cancelled",
        "not paid",
        "not available",
        "rejected",
        "completed",
      ].includes(visitStatus)
    ) {
      return false;
    }
    const isOngoingDuty = (isStarted && !isEnded) || isEnded;
    const isVisitOngoing = ["ongoing", "startduty", "endduty"].some((s) =>
      visitStatus.includes(s),
    );
    return isOngoingDuty || (!isStarted && !isEnded && isVisitOngoing);
  }
  if (filter === "Payment Pending") {
    return visitStatus === "payment pending";
  }
  if (filter === "Action Required") {
    return isLabResultPendingVisit(visit);
  }
  if (filter === "Completed") {
    return visitStatus === "completed" || visitStatus.includes("completed");
  }
  return false;
};

/* ---------------- Component ---------------- */

const Visits = ({
  searchTerm,
  bookingsData,
  partnerID: partnerIDProp,
  from,
  onRefresh,
}) => {
  const isReadOnly = bookingsData !== undefined;

  const [loading, setLoading] = useState(true);
  const [visitList, setVisitList] = useState([]);
  const [paginationPage, setPaginationPage] = useState(0);
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const saved = localStorage.getItem("visitsPageCount");
    return saved ? Number(saved) : 50;
  });
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [dutySlots, setDutySlots] = useState({});
  const [editingDutyIndex, setEditingDutyIndex] = useState(null);
  const [changingDutyStaff, setChangingDutyStaff] = useState({});
  const [availableDutyStaff, setAvailableDutyStaff] = useState([]);
  const [availableDutyStaffLoading, setAvailableDutyStaffLoading] =
    useState(false);
  const [availableDutyStaffSaving, setAvailableDutyStaffSaving] =
    useState(false);
  const [availableDutyStaffError, setAvailableDutyStaffError] = useState("");
  const dutyStaffRequestRef = useRef(0);

  useEffect(
    () => () => {
      dutyStaffRequestRef.current += 1;
    },
    [],
  );

  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [timeModalDutyIndex, setTimeModalDutyIndex] = useState(null);
  const [timeModalDutyKey, setTimeModalDutyKey] = useState(null);
  const [timeModalSelectedSlot, setTimeModalSelectedSlot] = useState("");

  const [reportsModalOpen, setReportsModalOpen] = useState(false);
  const [reportVisit, setReportVisit] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [openingReportVisitId, setOpeningReportVisitId] = useState("");
  const [openAssessments, setOpenAssessments] = useState({});
  const [isTreatmentPlanOpen, setIsTreatmentPlanOpen] = useState(false);
  const [labFailureTarget, setLabFailureTarget] = useState(null);
  const [labFailureReason, setLabFailureReason] = useState("");
  const [labFailureSaving, setLabFailureSaving] = useState(false);

  const isReportVisitPhysiotherapist = useMemo(() => {
    const r = String(reportVisit?.specialistInfo?.role || "")
      .trim()
      .toLowerCase();
    return r === "physiotherapist";
  }, [reportVisit]);

  const isPhysioAssessment = useMemo(() => {
    if (!reportVisit) return false;

    const assessmentArray = reportVisit.assessment || [];
    const hasActiveAssessment =
      Array.isArray(assessmentArray) &&
      assessmentArray.some((a) => a.active === true);

    return isReportVisitPhysiotherapist && hasActiveAssessment;
  }, [reportVisit, isReportVisitPhysiotherapist]);

  const isReportVisitLab =
    String(reportVisit?.serviceCategory || "").trim().toLowerCase() === "lab" ||
    String(reportVisit?.specialistInfo?.role || "").trim().toLowerCase() ===
      "phlebotomist";
  const isExplicitLabReport =
    String(reportVisit?.serviceCategory || "").trim().toLowerCase() === "lab";

  const completedReportAssessments = useMemo(() => {
    return (reportVisit?.assessment || []).filter(
      (assess) => assess?.completed,
    );
  }, [reportVisit?.assessment]);

  const [showTracking, setShowTracking] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(() => {
    if (from) {
      if (from === "Live") return ["Ongoing"];
      return [from];
    }
    return ["All"];
  });
  const [activeServiceTab, setActiveServiceTab] = useState(
    SERVICE_TAB_HOMECARE,
  );
  const [exportingPdf, setExportingPdf] = useState(false);
  const [labResultUploadingVisitId, setLabResultUploadingVisitId] =
    useState("");
  const [labResultPreview, setLabResultPreview] = useState(null);
  const labResultInputRef = useRef(null);
  const labResultTargetVisitIdRef = useRef("");
  const openAllReportsModalRef = useRef(null);

  const handleFilterChange = (filterName) => {
    setPaginationPage(0);
    setSelectedFilters((prev) => {
      const isAllActive = prev.includes("All");

      if (filterName === "All") {
        if (isAllActive) {
          return [];
        }
        return ["All"];
      }

      let next = new Set(prev);
      if (isAllActive) {
        next.delete("All");
        next.add("Ongoing");
        next.add("Upcoming");
        next.add("Payment Pending");
        next.add("Action Required");
      }

      if (next.has(filterName)) {
        next.delete(filterName);
      } else {
        next.add(filterName);
      }

      if (
        next.has("Ongoing") &&
        next.has("Upcoming") &&
        next.has("Payment Pending") &&
        next.has("Action Required")
      ) {
        next.delete("Ongoing");
        next.delete("Upcoming");
        next.delete("Payment Pending");
        next.delete("Action Required");
        next.add("All");
      }

      return Array.from(next);
    });
  };

  // ✅ Date edit modal states
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [dateModalDutyIndex, setDateModalDutyIndex] = useState(null);
  const [dateModalDutyKey, setDateModalDutyKey] = useState(null);
  const [dateModalMinDate, setDateModalMinDate] = useState("");
  const [dateModalSelectedDate, setDateModalSelectedDate] = useState(null);

  // ✅ Dynamic slots states for rescheduling
  const [dynamicSlots, setDynamicSlots] = useState([]);
  const [dynamicSlotsLoading, setDynamicSlotsLoading] = useState(false);
  const [dynamicSlotsError, setDynamicSlotsError] = useState("");

  // ✅ Visit Cancellation modal states
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelModalDutyIndex, setCancelModalDutyIndex] = useState(null);
  const [cancelModalDutyKey, setCancelModalDutyKey] = useState(null);
  const [cancelFormSubTitle, setCancelFormSubTitle] = useState("");
  const [cancelFormDate, setCancelFormDate] = useState("");
  const [cancelFormReason, setCancelFormReason] = useState("");
  const [cancelFormDescription, setCancelFormDescription] = useState("");
  const [cancelFormAuthorizerName, setCancelFormAuthorizerName] = useState("");
  const [notAvailableReasons, setNotAvailableReasons] = useState([]);

  // ✅ Duty Close modal states
  const [dutyCloseModalOpen, setDutyCloseModalOpen] = useState(false);
  const [dutyCloseModalDutyIndex, setDutyCloseModalDutyIndex] = useState(null);
  const [dutyCloseModalDutyKey, setDutyCloseModalDutyKey] = useState(null);
  const [dutyCloseType, setDutyCloseType] = useState("");

  useEffect(() => {
    if (from) {
      if (from === "Live") setSelectedFilters(["Ongoing"]);
      else setSelectedFilters([from]);
    } else {
      setSelectedFilters(["All"]);
    }
    // getLookup();
  }, [partnerIDProp, bookingsData, from]);

  const fetchVisits = async () => {
    if (bookingsData !== undefined)
      return Array.isArray(bookingsData) ? bookingsData : [];

    const result = await getAssignedStaffList();

    return Array.isArray(result?.data) ? result.data : [];
  };

  // const getLookup = async () => {
  //     try {
  //         const result = await getLookupData();
  //         const data = Array.isArray(result?.data) ? result.data : [];

  //         const doc = data.find((res) => res?.type === "notAvailableReasons");
  //         const reasons = Array.isArray(doc?.values) ? doc.values : [];

  //         setNotAvailableReasons(reasons);
  //         console.log(reasons, "notAvailableReasons");
  //     } catch (e) {
  //         console.error("getLookup error:", e);
  //         setNotAvailableReasons([]);
  //     }
  // };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const list = await fetchVisits();
        if (!cancelled) setVisitList(list);
      } catch (error) {
        console.error("Error fetching assignment data:", error);
        if (!cancelled) setVisitList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerIDProp, bookingsData]);

  const serviceTabCounts = useMemo(
    () =>
      (Array.isArray(visitList) ? visitList : []).reduce(
        (acc, visit) => {
          if (isLabServiceVisit(visit) && !hasRenderableVisitRows(visit)) {
            return acc;
          }

          const visitTab = isLabServiceVisit(visit)
            ? SERVICE_TAB_LAB
            : SERVICE_TAB_HOMECARE;
          acc[visitTab] += 1;
          return acc;
        },
        { [SERVICE_TAB_HOMECARE]: 0, [SERVICE_TAB_LAB]: 0 },
      ),
    [visitList],
  );
  const hasLabVisits = serviceTabCounts[SERVICE_TAB_LAB] > 0;

  useEffect(() => {
    if (!hasLabVisits && activeServiceTab === SERVICE_TAB_LAB) {
      setActiveServiceTab(SERVICE_TAB_HOMECARE);
    }
  }, [hasLabVisits, activeServiceTab]);

  const filteredData = useMemo(() => {
    let baseList = Array.isArray(visitList) ? [...visitList] : [];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      baseList = baseList.filter((item) => {
        const name =
          item?.patient?.name?.toLowerCase() ||
          item?.patientInfo?.personalInfo?.basicInfo?.nameInfo?.fullName?.toLowerCase() ||
          "";
        const phone = String(
          item?.patient?.phone ?? item?.patientInfo?.phone ?? "",
        );
        return name.includes(term) || phone.includes(term);
      });
    }

    if (activeServiceTab === SERVICE_TAB_LAB) {
      baseList = baseList.filter(isLabServiceVisit);
    } else {
      baseList = baseList.filter((visit) => !isLabServiceVisit(visit));
    }

    const flatList = [];
    baseList.forEach((visit) => {
      const duties = getDisplayDuties(visit);
      const sortedDutiesOfVisit = [...duties].sort((a, b) => {
        const valA = a?.date ? moment(a.date).valueOf() : 0;
        const valB = b?.date ? moment(b.date).valueOf() : 0;
        return valA - valB;
      });
      sortedDutiesOfVisit.forEach((duty, dutyIndex) => {
        flatList.push({
          ...visit,
          _id:
            duty._id ||
            `virtual-${normalizeId(visit._id)}-${duty.date || "unknown"}`,
          selectedDuty: duty,
          parentVisit: visit,
          dutyDayNumber: duty.day || dutyIndex + 1,
          totalDutyDays: sortedDutiesOfVisit.length,
        });
      });
    });

    const finalFiltered = flatList.filter((row) => {
      const visit = row.parentVisit || row;
      const duty = row.selectedDuty;

      return selectedFilters.some((filterName) => {
        if (["All", "Upcoming", "Ongoing"].includes(filterName)) {
          if (duty?.date) {
            const dutyMoment = moment(duty.date).startOf("day");
            const todayMoment = moment().startOf("day");
            if (dutyMoment.isBefore(todayMoment)) {
              if (
                filterName === "All" &&
                (matchesFilter(visit, duty, "Payment Pending") ||
                  matchesFilter(visit, duty, "Action Required"))
              ) {
                return true;
              }
              return false;
            }
          }
        }
        return matchesFilter(visit, duty, filterName);
      });
    });

    return finalFiltered;
  }, [visitList, searchTerm, selectedFilters, activeServiceTab]);

  const handleExportDetailedPdf = async () => {
    if (exportingPdf) return;

    const exportSource =
      Array.isArray(filteredData) && filteredData.length > 0
        ? filteredData
        : visitList;
    const exportable = filterExportableBookings(exportSource);

    if (!exportable.length) {
      showToast(
        "No accepted, startDuty, endDuty, or completed bookings found for export.",
        "Error",
      );
      return;
    }

    setExportingPdf(true);
    try {
      const partnerResponse = await getPartner(
        getPartnerIdForExport(exportable[0]),
      );
      const result = await downloadDetailedBookingsPdf({
        visits: exportable,
        partner: partnerResponse?.data || null,
        sourceLabel: "Partner Visits",
      });
      showToast(
        `Exported ${result.count} booking(s) to ${result.fileName}`,
        "Success",
      );
    } catch (error) {
      console.error("Visits detailed PDF export failed:", error);
      showToast(
        error?.message || "Failed to export detailed booking PDF.",
        "Error",
      );
    } finally {
      setExportingPdf(false);
    }
  };

  const selectedId = normalizeId(selectedPatient?._id);

  useEffect(() => {
    if (!filteredData.length) {
      if (selectedPatient !== null) setSelectedPatient(null);
      return;
    }

    if (!selectedId) {
      setSelectedPatient(filteredData[0]);
      return;
    }

    const updated = filteredData.find(
      (x) => normalizeId(x?._id) === selectedId,
    );
    if (!updated) {
      setSelectedPatient(filteredData[0]);
      return;
    }
    if (updated !== selectedPatient) setSelectedPatient(updated);
  }, [filteredData, selectedId]); // intentionally NOT depending on selectedPatient reference

  // ✅ FIX: duties for Requests come from visitInfo if dutyInfo empty
  const sortedDuties = useMemo(() => {
    const arr = getDisplayDuties(selectedPatient);
    return [...arr].sort((a, b) => {
      const valA = a?.date ? moment(a.date).valueOf() : 0;
      const valB = b?.date ? moment(b.date).valueOf() : 0;
      return valA - valB;
    });
  }, [selectedPatient]);

  const sortedReportDuties = useMemo(() => {
    const arr = getDisplayDuties(reportVisit);
    return [...arr].sort((a, b) => {
      const valA = a?.date ? moment(a.date).valueOf() : 0;
      const valB = b?.date ? moment(b.date).valueOf() : 0;
      return valA - valB;
    });
  }, [reportVisit]);

  const dischargeSummariesForHistory = useMemo(() => {
    const summaryDoc = (selectedPatient?.parentVisit || selectedPatient)
      ?.visitInfo?.dischargeSummaryDoc;
    if (!summaryDoc) return [];
    if (Array.isArray(summaryDoc))
      return summaryDoc.filter((item) => item && typeof item === "object");
    return typeof summaryDoc === "object" ? [summaryDoc] : [];
  }, [selectedPatient]);

  const handleRowClick = (patient) => {
    setSelectedPatient(patient);
    setDutySlots({});
    dutyStaffRequestRef.current += 1;
    setEditingDutyIndex(null);
    setChangingDutyStaff({});
    setAvailableDutyStaff([]);
    setAvailableDutyStaffLoading(false);
    setAvailableDutyStaffSaving(false);
    setAvailableDutyStaffError("");
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");
    setShowTracking(false);
    setTrackingData(null);
  };

  // ✅ compute "min date" = (last duty date + 1)
  const computeMinRescheduleDate = (duties) => {
    const list = Array.isArray(duties) ? duties : [];
    const maxMoment = list.reduce((acc, d) => {
      const m = moment(d?.date);
      if (!m.isValid()) return acc;
      if (!acc) return m;
      return m.isAfter(acc) ? m : acc;
    }, null);

    const min =
      maxMoment && maxMoment.isValid()
        ? maxMoment.clone().add(1, "day")
        : moment().add(1, "day");

    return min.format("YYYY-MM-DD");
  };

  const openDateModal = (e, dutyIndex, dutyKey) => {
    e?.stopPropagation?.();

    const duty = sortedDuties?.[dutyIndex];
    if (!duty) return;

    const canEdit = canEditDutyDateAndStaff(
      selectedPatient?.parentVisit || selectedPatient,
      duty,
    );

    if (!canEdit) {
      showToast(
        "Date change allowed only for editable status and upcoming/today duties.",
        "Error",
      );
      return;
    }

    const minDate = moment().format("YYYY-MM-DD");
    const initialDate = duty?.date ? moment(duty.date).toDate() : new Date();

    setDateModalDutyIndex(dutyIndex);
    setDateModalDutyKey(dutyKey);
    setDateModalMinDate(minDate);
    setDateModalSelectedDate(initialDate);
    setDateModalOpen(true);
  };

  const closeDateModal = () => {
    setDateModalOpen(false);
    setDateModalDutyIndex(null);
    setDateModalDutyKey(null);
    setDateModalMinDate("");
    setDateModalSelectedDate(null);
  };

  // ✅ Reschedule date using existing timeSlotChange API (keeps same time)
  const applySelectedDutyDate = async () => {
    const visit = selectedPatient?.parentVisit || selectedPatient;
    if (!visit) return;
    if (dateModalDutyIndex == null) return;
    if (!dateModalSelectedDate) return;

    const duty = sortedDuties?.[dateModalDutyIndex];
    if (!duty) return;

    const canEdit = canEditDutyDateAndStaff(visit, duty);
    if (!canEdit) {
      showToast("Cannot change date for this duty.", "Error");
      return;
    }

    // ✅ enforce min date
    if (
      dateModalMinDate &&
      moment(dateModalSelectedDate).isBefore(moment(dateModalMinDate), "day")
    ) {
      showToast(
        `Please select a date on/after ${moment(dateModalMinDate).format("DD/MM/YYYY")}`,
        "Error",
      );
      return;
    }

    const newDateStr = moment(dateModalSelectedDate).format("YYYY-MM-DD");
    const oldDateStr = moment(duty?.date).format("YYYY-MM-DD");

    // ✅ keep existing time slot for that duty
    const dutyKey = dateModalDutyKey;
    const currentLabel = getDutyTimeLabel(visit, duty, dutySlots, dutyKey);

    // parse slot label like "06:00 AM to 07:00 AM"
    let { startTimeStr, endTimeStr } = parseSelectedSlot(currentLabel);

    // fallback to visit slot if currentLabel is "-"
    if (!startTimeStr || !endTimeStr) {
      const fallbackLabel = visit?.visitInfo?.slot || "";
      const parsed = parseSelectedSlot(fallbackLabel);
      startTimeStr = startTimeStr || parsed.startTimeStr;
      endTimeStr = endTimeStr || parsed.endTimeStr;
    }

    // fallback to duty start/end time
    if ((!startTimeStr || !endTimeStr) && duty?.startTime && duty?.endTime) {
      const st = duty?.startTime?.$date || duty?.startTime;
      const et = duty?.endTime?.$date || duty?.endTime;
      if (st && et) {
        startTimeStr = moment(st).local().format("hh:mm A");
        endTimeStr = moment(et).local().subtract(1, "hour").format("hh:mm A");
      }
    }

    if (!startTimeStr || !endTimeStr) {
      showToast(
        "Cannot reschedule: time slot not found for this duty.",
        "Error",
      );
      return;
    }

    const startMoment = moment(
      `${newDateStr} ${startTimeStr}`,
      "YYYY-MM-DD hh:mm A",
    );
    const endMoment = moment(
      `${newDateStr} ${endTimeStr}`,
      "YYYY-MM-DD hh:mm A",
    );

    // Prepare payload for the new dateSlotChange API
    const payload = {
      visitID: visit._id,
      visitDate: moment(oldDateStr, "YYYY-MM-DD").format("DD/MM/YYYY"),
      changeDate: moment(newDateStr, "YYYY-MM-DD").format("DD/MM/YYYY"),
    };

    try {
      const response = await dateSlotChange(payload);
      //   console.log(response.data);
      if (response?.error !== true) {
        showToast("Visit date updated successfully", "Success");
        closeDateModal();
        await refetchVisits();
      } else {
        console.log("API error:", response?.message);
        showToast(response?.message || "Failed to update visit date", "Error");
      }
    } catch (err) {
      console.error("Error updating visit date:", err);
      showToast("Something went wrong while updating visit date", "Error");
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "-";
    const age = moment().diff(moment(dob), "years");
    return `${age} yrs`;
  };
  // console.log(selectedPatient);
  const isPhysiotherapist =
    (selectedPatient?.parentVisit || selectedPatient)?.specialistInfo?.role ===
    "physiotherapist";

  const openDutyStaffEditor = async (dutyIndex, dutyKey) => {
    if (isReadOnly || availableDutyStaffSaving) return;
    const visit = selectedPatient?.parentVisit || selectedPatient;
    const duty = sortedDuties?.[dutyIndex];
    const visitID = normalizeId(visit?._id);
    const dutyID = normalizeId(duty?._id);
    if (!visitID || !dutyID) {
      showToast("Visit and duty information are required", "Error");
      return;
    }

    const requestID = dutyStaffRequestRef.current + 1;
    dutyStaffRequestRef.current = requestID;
    setEditingDutyIndex(dutyIndex);
    setChangingDutyStaff((previous) => ({
      ...previous,
      [dutyIndex]: "",
    }));
    setAvailableDutyStaff([]);
    setAvailableDutyStaffError("");
    setAvailableDutyStaffLoading(true);

    try {
      const result = await getAvailableDutyHCPs(visitID, dutyID);
      if (dutyStaffRequestRef.current !== requestID) return;
      if (result?.error) {
        throw new Error(result?.message || "Failed to load available staff");
      }

      const specialists = Array.isArray(result?.data?.specialists)
        ? result.data.specialists
        : [];
      setAvailableDutyStaff(
        specialists.map((specialist) => ({
          id: normalizeId(specialist?._id),
          name: specialist?.name || "Unnamed specialist",
          role: specialist?.role,
          subRole: specialist?.subRole,
        })),
      );
    } catch (error) {
      if (dutyStaffRequestRef.current !== requestID) return;
      setAvailableDutyStaff([]);
      setAvailableDutyStaffError(
        error?.message || "Failed to load available staff",
      );
    } finally {
      if (dutyStaffRequestRef.current === requestID) {
        setAvailableDutyStaffLoading(false);
      }
    }
  };

  const refetchVisits = async () => {
    if (bookingsData !== undefined) {
      if (onRefresh) {
        setLoading(true);
        try {
          await onRefresh();
        } finally {
          setLoading(false);
        }
      }
      return;
    }
    setLoading(true);
    try {
      const list = await fetchVisits();
      setVisitList(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDuty = (dutyIndex, dutyKey) => {
    if (isReadOnly) return;
    const duty = sortedDuties?.[dutyIndex];
    if (!duty) return;

    // Pre-fill date from duty
    const autoDate = duty.date
      ? moment(duty.date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    setCancelModalDutyIndex(dutyIndex);
    setCancelModalDutyKey(dutyKey);
    setCancelFormSubTitle("");
    setCancelFormDate(autoDate);
    setCancelFormReason("");
    setCancelFormDescription("");
    setCancelFormAuthorizerName("");
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setCancelModalDutyIndex(null);
    setCancelModalDutyKey(null);
    setCancelFormSubTitle("");
    setCancelFormDate("");
    setCancelFormReason("");
    setCancelFormDescription("");
    setCancelFormAuthorizerName("");
  };

  const submitCancelDuty = async () => {
    if (!selectedPatient) return;
    const duty = sortedDuties?.[cancelModalDutyIndex];
    if (!duty) return;

    if (!cancelFormReason) {
      showToast("Please select a reason.", "Error");
      return;
    }
    if (!cancelFormAuthorizerName.trim()) {
      showToast("Please enter the authorizer name.", "Error");
      return;
    }

    try {
      // API call will be implemented here later
      //   console.log("Submitting duty cancellation:", {
      //     visitID: selectedPatient?.parentVisit?._id || selectedPatient?._id,
      //     dutyID: duty._id,
      //     dutyKey: cancelModalDutyKey,
      //     date: cancelFormDate,
      //     subTitle: cancelFormSubTitle,
      //     reason: cancelFormReason,
      //     description: cancelFormDescription,
      //     authorizerName: cancelFormAuthorizerName,
      //   });

      showToast("Duty cancellation submitted successfully", "Success");
      closeCancelModal();
      await refetchVisits();
    } catch (err) {
      console.error("Error cancelling duty:", err);
      showToast("Failed to submit cancellation. Please try again.", "Error");
    }
  };

  const handleOpenDutyCloseModal = (dutyIndex, dutyKey) => {
    setDutyCloseModalDutyIndex(dutyIndex);
    setDutyCloseModalDutyKey(dutyKey);
    setDutyCloseType("");
    setDutyCloseModalOpen(true);
  };

  const closeDutyCloseModal = () => {
    setDutyCloseModalOpen(false);
    setDutyCloseModalDutyIndex(null);
    setDutyCloseModalDutyKey(null);
    setDutyCloseType("");
  };

  const submitDutyClose = async () => {
    if (!dutyCloseType) {
      showToast("Please select a close type.", "Error");
      return;
    }
    try {
      const visit = selectedPatient?.parentVisit || selectedPatient;
      const visitID = normalizeId(visit?._id);
      const partnerID = selectedPatient?.appointmentType?.partnerID || "";
      const duty = sortedDuties?.[dutyCloseModalDutyIndex];
      const date = duty?.date;

      const apiBody = {
        visitID,
        partnerID,
        date,
        actionType: dutyCloseType,
      };

      //   console.log("Submitting duty close:", apiBody);
      const req = await closeDuty(apiBody);
      //   console.log(req);
      if (req.error === false) {
        showToast(
          req.message || "Duty close submitted successfully",
          "Success",
        );
        closeDutyCloseModal();
        await refetchVisits();
      } else {
        showToast(
          req.message || "Failed to close duty. Please try again.",
          "Error",
        );
      }
    } catch (err) {
      console.error("Error closing duty:", err);
      showToast("Failed to close duty. Please try again.", "Error");
    }
  };

  const handleDutyStaffChange = async (dutyIndex, staffId) => {
    if (isReadOnly || availableDutyStaffSaving) return;
    const visit = selectedPatient?.parentVisit || selectedPatient;
    if (!visit || !staffId) return;

    const duty = sortedDuties?.[dutyIndex];
    if (!duty) return;

    const canEdit = canEditDutyDateAndStaff(visit, duty);

    if (!canEdit) {
      showToast(
        "HCP change allowed only for editable status and upcoming/today duties.",
        "Error",
      );
      return;
    }

    const selectedStaff = availableDutyStaff.find((s) => s.id === staffId);
    if (!selectedStaff) {
      showToast("Selected staff not found", "Error");
      return;
    }

    const fromName = getDutyHcpName(visit, duty);
    const confirmChange = window.confirm(
      `Are you sure you want to change staff for ${moment(duty.date).format(
        "DD/MM/YYYY",
      )}?\n\nFrom: ${fromName}\nTo: ${selectedStaff.name}`,
    );
    if (!confirmChange) {
      setChangingDutyStaff((previous) => ({
        ...previous,
        [dutyIndex]: "",
      }));
      return;
    }

    const payload = {
      visitID: visit._id,
      dutyID: normalizeId(duty?._id),
      specialistID: selectedStaff.id,
      date: moment(duty.date).format("YYYY-MM-DD"),
    };

    setAvailableDutyStaffSaving(true);
    console.log(payload);
    try {
      const result = await actingHCPChange(payload);
      console.log(result, "ss");
      if (!result || result?.error !== false) {
        throw new Error(
          result?.message || "Failed to change staff. Please try again.",
        );
      }
      showToast("Staff changed successfully!", "Success");

      dutyStaffRequestRef.current += 1;
      setEditingDutyIndex(null);
      setAvailableDutyStaff([]);
      setAvailableDutyStaffError("");
      setChangingDutyStaff((prev) => {
        const copy = { ...prev };
        delete copy[dutyIndex];
        return copy;
      });

      await refetchVisits();
    } catch (err) {
      console.error(err);
      showToast(
        err?.message || "Failed to change staff. Please try again.",
        "Error",
      );
    } finally {
      setAvailableDutyStaffSaving(false);
    }
  };

  const handleCloseDropdown = (dutyIndex) => {
    dutyStaffRequestRef.current += 1;
    setEditingDutyIndex(null);
    setAvailableDutyStaff([]);
    setAvailableDutyStaffLoading(false);
    setAvailableDutyStaffSaving(false);
    setAvailableDutyStaffError("");
    setChangingDutyStaff((prev) => {
      const copy = { ...prev };
      delete copy[dutyIndex];
      return copy;
    });
  };

  const openTimeModal = async (dutyIndex, dutyKey, currentSlot) => {
    if (isReadOnly) return;

    const visit = selectedPatient?.parentVisit || selectedPatient;
    if (!visit) return;

    const duty = sortedDuties?.[dutyIndex];
    if (!duty) return;

    const canEdit = canEditDutyTime(visit, duty);

    if (!canEdit) {
      showToast(
        "Time slot change allowed only for editable status and upcoming duties.",
        "Error",
      );
      return;
    }

    const alertMsg =
      "Alert: Booking Slot Change Notice\n\n" +
      "You are attempting to change the scheduled booking slot for an assigned visit.\n" +
      "Please ensure that any changes are made only after clear communication and mutual agreement between you and the patient.\n\n" +
      'Kindly note that this process is solely between you and the patient. We ("Ceviliyar") do not participate in, monitor, or assume responsibility for any booking slot changes made.';

    const proceed = window.confirm(alertMsg);
    if (!proceed) return;

    setTimeModalDutyIndex(dutyIndex);
    setTimeModalDutyKey(dutyKey);
    setTimeModalSelectedSlot("");
    setTimeModalOpen(true);

    try {
      setDynamicSlotsLoading(true);
      setDynamicSlotsError("");
      setDynamicSlots([]);

      const partnerID = normalizeId(
        visit?.appointmentType?.partnerID || visit?.partnerID,
      );
      const role = visit?.specialistInfo?.role || "";
      const isPhysio = role === "physiotherapist";
      const startDate = moment(duty.date).format("DD/MM/YYYY");
      const visitID = visit?._id;
      const dutyID = normalizeId(duty?._id);
      const serviceType = visit?.dutyType;

      let res;
      if (isPhysio) {
        res = await getPhysioReshecduleSlots(
          partnerID,
          visitID,
          dutyID,
          startDate,
        );
      } else {
        res = await getNurseReshecduleSlots(
          partnerID,
          visitID,
          dutyID,
          startDate,
          serviceType,
        );

        // console.log(res);
      }

      if (res && res.error === false) {
        let slotValues = [];
        if (res?.data?.slots && Array.isArray(res.data.slots)) {
          const isNested = res.data.slots.some(
            (group) => group && Array.isArray(group.slots),
          );
          if (isNested) {
            res.data.slots.forEach((group) => {
              if (group && Array.isArray(group.slots)) {
                group.slots.forEach((item) => {
                  if (item && item.slot) {
                    slotValues.push(item.slot);
                  }
                });
              }
            });
          } else {
            res.data.slots.forEach((item) => {
              if (item) {
                if (typeof item === "string") {
                  slotValues.push(item);
                } else if (item.slot) {
                  slotValues.push(item.slot);
                } else if (item.label) {
                  slotValues.push(item.label);
                }
              }
            });
          }
        } else {
          let flatArray = [];
          if (Array.isArray(res?.data)) {
            flatArray = res.data;
          } else if (Array.isArray(res?.slots)) {
            flatArray = res.slots;
          }

          flatArray.forEach((item) => {
            if (item) {
              if (typeof item === "string") {
                slotValues.push(item);
              } else if (item.slot) {
                slotValues.push(item.slot);
              } else if (item.label) {
                slotValues.push(item.label);
              }
            }
          });
        }

        let filteredSlots = slotValues
          .map((s) => String(s || "").trim())
          .filter(Boolean);

        if (duty?.date && isTodayDate(duty.date)) {
          const now = moment();
          const dutyDateStr = moment(duty.date).format("YYYY-MM-DD");
          filteredSlots = filteredSlots.filter((slotStr) => {
            const { startTimeStr } = parseSelectedSlot(slotStr);
            if (!startTimeStr) return true;
            const slotStart = moment(`${dutyDateStr} ${startTimeStr}`, [
              "YYYY-MM-DD hh:mm A",
              "YYYY-MM-DD h:mm A",
              "YYYY-MM-DD HH:mm",
            ]);
            if (!slotStart.isValid()) return true;
            return slotStart.isSameOrAfter(now);
          });
        }

        setDynamicSlots(filteredSlots);
      } else {
        setDynamicSlotsError(res?.message || "Failed to fetch slots");
      }
    } catch (err) {
      console.error("Error fetching dynamic slots:", err);
      setDynamicSlotsError("Error loading slots");
    } finally {
      setDynamicSlotsLoading(false);
    }
  };

  const closeTimeModal = () => {
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");
    setDynamicSlots([]);
    setDynamicSlotsError("");
  };

  const applySelectedTimeSlot = async () => {
    // console.log("wrk");
    if (isReadOnly) return;
    const visit = selectedPatient?.parentVisit || selectedPatient;
    if (!visit || timeModalDutyIndex == null || !timeModalSelectedSlot) return;

    const dutyForModal = sortedDuties?.[timeModalDutyIndex] || null;
    if (!dutyForModal) return;

    const canEdit = canEditDutyTime(visit, dutyForModal);
    if (!canEdit) {
      showToast("Cannot change time for this duty.", "Error");
      return;
    }

    const dateStr = moment(dutyForModal.date).format("YYYY-MM-DD");
    const { startTimeStr, endTimeStr } = parseSelectedSlot(
      timeModalSelectedSlot,
    );

    // If endTimeStr is empty, default to 11:59 PM
    const effectiveEndTimeStr =
      endTimeStr && String(endTimeStr).trim()
        ? String(endTimeStr).trim()
        : "11:59 PM";

    console.log(startTimeStr, effectiveEndTimeStr);

    if (!startTimeStr || !String(startTimeStr).trim()) {
      showToast("Invalid time slot", "Error");
      return;
    }

    const startMoment = moment(
      `${dateStr} ${startTimeStr}`,
      "YYYY-MM-DD hh:mm A",
    );
    const endMoment = moment(
      `${dateStr} ${effectiveEndTimeStr}`,
      "YYYY-MM-DD hh:mm A",
    );

    const payload = {
      visitID: visit._id,
      dutyID: normalizeId(dutyForModal?._id),
      startTime: startMoment.utc().format(),
      endTime: endMoment.utc().format(),
      date: dateStr,
    };

    // console.log(payload);

    try {
      const response = await timeSlotChange(payload);
      if (response?.error !== true) {
        showToast("Time slot updated successfully", "Success");
        if (timeModalDutyKey) {
          setDutySlots((prev) => ({
            ...prev,
            [timeModalDutyKey]: timeModalSelectedSlot,
          }));
        }
        closeTimeModal();
        await refetchVisits();
      } else {
        showToast("Failed to update time slot", "Error");
      }
    } catch (err) {
      console.error("Error updating time slot:", err);
      showToast("Something went wrong while updating time slot", "Error");
    }
  };

  const dutyForModal =
    timeModalDutyIndex != null ? sortedDuties?.[timeModalDutyIndex] : null;

  const openLiveTracking = (visitRow, duty) => {
    setTrackingData(buildLiveTrackingData(visitRow, duty));
    setShowTracking(true);
  };

  const openAllReportsModal = async (visit, selectedDuty = null) => {
    if (!visit) return;

    const visitId = normalizeId(visit?._id);
    setOpeningReportVisitId(visitId);

    let latestVisit = visit;
    try {
      if (bookingsData === undefined) {
        const list = await fetchVisits();
        if (Array.isArray(list)) {
          setVisitList(list);
          const freshMatch = list.find(
            (row) => normalizeId(row?._id) === visitId,
          );
          if (freshMatch) latestVisit = freshMatch;
        }
      }
    } catch (error) {
      console.error(
        "Failed to refresh latest report data before opening modal:",
        error,
      );
      showToast(
        "Unable to refresh latest report. Showing available data.",
        "Error",
      );
    } finally {
      setOpeningReportVisitId("");
    }

    setSelectedPatient(latestVisit);
    setReportVisit(latestVisit);
    setReportsModalOpen(true);

    const duties = getDisplayDuties(latestVisit);
    duties.sort(
      (a, b) => moment(a?.date).valueOf() - moment(b?.date).valueOf(),
    );

    const todayStr = moment().format("YYYY-MM-DD");
    const todayDuty = duties.find(
      (d) => moment(d?.date).format("YYYY-MM-DD") === todayStr,
    );

    let defaultDuty = todayDuty || duties[0];
    let defaultKey = "";
    if (selectedDuty) {
      const selectedDutyId = normalizeId(selectedDuty?._id);
      const selectedDutyDate = selectedDuty?.date
        ? moment(selectedDuty.date).format("YYYY-MM-DD")
        : null;
      const matchedIndex = duties.findIndex((d) => {
        if (selectedDutyId && normalizeId(d?._id) === selectedDutyId)
          return true;
        if (
          selectedDutyDate &&
          d?.date &&
          moment(d.date).format("YYYY-MM-DD") === selectedDutyDate
        )
          return true;
        return false;
      });
      if (matchedIndex !== -1) {
        defaultDuty = duties[matchedIndex];
        defaultKey = normalizeId(defaultDuty?._id) || String(matchedIndex);
      } else if (duties.length > 0) {
        defaultKey = normalizeId(duties[0]?._id) || "0";
      }
    } else if (defaultDuty) {
      const idx = duties.findIndex((d) => d === defaultDuty);
      defaultKey =
        normalizeId(defaultDuty?._id) || (idx !== -1 ? String(idx) : "0");
    }

    setExpandedDays(defaultKey ? { [defaultKey]: true } : {});
  };
  openAllReportsModalRef.current = openAllReportsModal;

  const closeAllReportsModal = () => {
    setReportsModalOpen(false);
    setReportVisit(null);
    setExpandedDays({});
    setOpenAssessments({});
    setIsTreatmentPlanOpen(false);
  };

  const closeLabFailureModal = () => {
    if (labFailureSaving) return;
    setLabFailureTarget(null);
    setLabFailureReason("");
  };

  const submitLabCollectionFailure = async () => {
    const failureReason = labFailureReason.trim();
    if (!labFailureTarget?.visitID || !labFailureTarget?.dutyID) return;
    if (!failureReason) {
      showToast("Please enter the collection failure reason.", "Error");
      return;
    }
    if (failureReason.length > 1000) {
      showToast("Failure reason must be 1000 characters or fewer.", "Error");
      return;
    }

    setLabFailureSaving(true);
    try {
      const result = await failLabCollection({
        visitID: labFailureTarget.visitID,
        dutyID: labFailureTarget.dutyID,
        failureReason,
      });
      if (result?.error !== false) {
        showToast(
          result?.message || "Unable to close the lab collection as failed.",
          "Error",
        );
        return;
      }
      showToast("Lab collection closed as failed.", "Success");
      setLabFailureTarget(null);
      setLabFailureReason("");
      closeAllReportsModal();
      await refetchVisits();
    } finally {
      setLabFailureSaving(false);
    }
  };

  const chooseLabResultFile = useCallback((visitID) => {
    const normalizedVisitID = normalizeId(visitID);
    if (!normalizedVisitID || labResultUploadingVisitId) return;
    labResultTargetVisitIdRef.current = normalizedVisitID;
    labResultInputRef.current?.click();
  }, [labResultUploadingVisitId]);

  useEffect(() => {
    const previewUrl = labResultPreview?.previewUrl;
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [labResultPreview?.previewUrl]);

  const closeLabResultPreview = () => {
    if (labResultUploadingVisitId) return;
    setLabResultPreview(null);
  };

  const handleLabResultFileChange = (event) => {
    const file = event.target.files?.[0];
    const visitID = labResultTargetVisitIdRef.current;
    event.target.value = "";
    labResultTargetVisitIdRef.current = "";

    if (!file || !visitID) return;

    const fileName = String(file.name || "").toLowerCase();
    const isPdf = file.type === "application/pdf" || fileName.endsWith(".pdf");
    if (!isPdf) {
      showToast("Please select a PDF lab result.", "Error");
      return;
    }
    if (
      Number(file.size || 0) <= 0 ||
      Number(file.size || 0) > 10 * 1024 * 1024
    ) {
      showToast("Lab result PDF must be 10 MB or smaller.", "Error");
      return;
    }

    setLabResultPreview({
      file,
      visitID,
      previewUrl: URL.createObjectURL(file),
    });
  };

  const confirmLabResultUpload = async () => {
    const file = labResultPreview?.file;
    const visitID = labResultPreview?.visitID;
    if (!file || !visitID || labResultUploadingVisitId) return;

    setLabResultUploadingVisitId(visitID);
    try {
      const result = await uploadLabResult({ visitID, file });
      if (result?.error !== false) {
        showToast(result?.message || "Unable to upload the lab result.", "Error");
        return;
      }

      showToast("Lab result uploaded successfully.", "Success");
      setLabResultPreview(null);
      await refetchVisits();
    } catch (error) {
      console.error("Lab result upload failed:", error);
      showToast("Unable to upload the lab result.", "Error");
    } finally {
      setLabResultUploadingVisitId("");
    }
  };

  const openLabResult = useCallback(async (visitID) => {
    const normalizedVisitID = normalizeId(visitID);
    if (!normalizedVisitID) return;

    try {
      const result = await getLabResultUrlForAdmin(normalizedVisitID);
      const url = result?.data?.url;
      if (result?.error !== false || !isSafeLabResultUrl(url)) {
        showToast(result?.message || "Unable to load the lab result.", "Error");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Lab result open failed:", error);
      showToast("Unable to load the lab result.", "Error");
    }
  }, []);

  const toggleDay = (dutyKey) => {
    setExpandedDays((prev) => ({ ...prev, [dutyKey]: !prev?.[dutyKey] }));
  };

  const columns = useMemo(() => {
    return [
      {
        Header: () => (
          <div className="font-poppins ml-4 whitespace-nowrap text-[13px]">
            Name
          </div>
        ),
        accessor: "patient.name",
        Cell: ({ row: { original } }) => (
          <div className="font-poppins flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                normalizeId(selectedPatient?._id) === normalizeId(original?._id)
                  ? "bg-blue-600"
                  : "bg-transparent"
              }`}
            />
            <p className="text-blue-600 truncate max-w-[200px] text-[13px]">
              {original?.patient?.name || "N/A"}
            </p>
          </div>
        ),
      },

      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">Date</div>
        ),
        accessor: "selectedDuty.date",
        Cell: ({ row: { original } }) => {
          const duty = original?.selectedDuty;
          const dateStr = duty?.displayDateRange
            ? duty.displayDateRange
            : duty?.date
              ? moment(duty.date).format("DD/MM/YYYY")
              : "-";
          return <div className="font-poppins text-[13px]">{dateStr}</div>;
        },
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">Day</div>
        ),
        id: "day",
        Cell: ({ row: { original } }) => {
          const duty = original?.selectedDuty;
          const dayString = original?.totalDutyDays
            ? `${original.dutyDayNumber} / ${original.totalDutyDays}`
            : `${original.dutyDayNumber}`;
          return (
            <div className="font-poppins text-[13px]">
              {duty?.displayDateRange
                ? "-"
                : original?.dutyDayNumber
                  ? dayString
                  : "-"}
            </div>
          );
        },
      },
      // {
      //     Header: () => <div className="font-poppins whitespace-nowrap text-[13px]">Time</div>,
      //     accessor: "selectedDuty.slot",
      //     Cell: ({ row: { original } }) => {
      //         const duty = original?.selectedDuty;
      //         const dutyKey = original?._id;
      //         const displayTime = getDutyTimeLabel(original?.parentVisit || original, duty, dutySlots, dutyKey);
      //         return (
      //             <div className="font-poppins text-[13px] font-medium text-gray-800">
      //                 {displayTime}
      //             </div>
      //         );
      //     },
      // },
      // {
      //   Header: () => (
      //     <div className="font-poppins whitespace-nowrap text-[13px]">
      //       Gender
      //     </div>
      //   ),
      //   accessor: "patient.gender",
      //   Cell: ({ row: { original } }) => (
      //     <div className="font-poppins text-[13px]">
      //       {original?.patient?.gender
      //         ? capitalizeFirstLetter(original.patient.gender)
      //         : "-"}
      //     </div>
      //   ),
      // },
      // {
      //   Header: () => (
      //     <div className="font-poppins whitespace-nowrap text-[13px]">Age</div>
      //   ),
      //   accessor: "patient.dob",
      //   Cell: ({ row: { original } }) => (
      //     <div className="font-poppins">
      //       {calculateAge(
      //         original?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.dob ||
      //           null,
      //       )}
      //     </div>
      //   ),
      // },

      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">
            Service
          </div>
        ),
        accessor: "specialistInfo.role",
        Cell: ({ row: { original } }) => (
          <div className="text-[13px] font-poppins truncate max-w-[160px]">
            {isLabServiceVisit(original)
              ? "Phlebotomist"
              : original?.specialistInfo?.subRole
              ? capitalizeFirstLetter(
                  original.specialistInfo.subRole === "nursing-assistant"
                    ? "Nursing Assistant"
                    : original.specialistInfo.subRole === "phlebotomist"
                      ? "Lab test or package"
                      : original.specialistInfo.subRole,
                )
              : "-"}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">
            Location
          </div>
        ),
        accessor: "visitInfo.addressInfo.city",
        Cell: ({ row: { original } }) => (
          <div className="text-[12px] font-poppins truncate max-w-[160px] text-[13px]">
            {original?.visitInfo?.addressInfo?.city
              ? capitalizeFirstLetter(original.visitInfo.addressInfo.city)
              : "-"}
          </div>
        ),
      },
      {
        id: "dutyStaff",
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">
            Duty Staff
          </div>
        ),
        accessor: (row) => {
          const name = getDutyHcpName(
            row?.parentVisit || row,
            row?.selectedDuty,
          );
          return name === "N/A" ? "" : name;
        },
        disableSortBy: true,
        Cell: ({ value }) => (
          <div className="font-poppins text-blue-600">
            <p className="truncate max-w-[140px] text-[13px]">
              <span className="text-[#4361EE] font-medium">{value || "-"}</span>
            </p>
          </div>
        ),
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap ml-2 text-[13px]">
            Status
          </div>
        ),
        accessor: "status",
        Cell: ({ row: { original } }) => (
          <div className="flex justify-start">
            <StatusBadge
              status={getDutyStatusLabel(
                original?.parentVisit || original,
                original?.selectedDuty,
              )}
            />
          </div>
        ),
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-[13px]">
            Report
          </div>
        ),
        accessor: "report",
        disableSortBy: true,
        Cell: ({ row: { original } }) => {
          const rowVisitId = normalizeId(
            original?.parentVisit?._id || original?._id,
          );
          const visit = original?.parentVisit || original;
          const visitStatus = String(visit?.status || "")
            .trim()
            .toLowerCase();
          const isLabVisit =
            String(visit?.serviceCategory || "").trim().toLowerCase() === "lab";
          const isOpening = openingReportVisitId === rowVisitId;
          if (isLabVisit) {
            return (
              <button
                type="button"
                disabled={isOpening}
                onClick={(e) => {
                  e.stopPropagation();
                  openAllReportsModalRef.current?.(
                    visit,
                    original?.selectedDuty,
                  );
                }}
                className={`font-medium font-poppins text-[12px] underline ${
                  isOpening
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-[#4361EE] hover:opacity-80"
                }`}
              >
                {isOpening ? "Loading..." : "View Report"}
              </button>
            );
          }
          const isPaymentPending =
            visitStatus === "payment pending" ||
            getVisitStatusLabel(visit) === "Payment pending";
          const isDisabled = isOpening || isPaymentPending;
          return (
            <button
              type="button"
              disabled={isDisabled}
              onClick={(e) => {
                e.stopPropagation();
                openAllReportsModalRef.current?.(
                  original?.parentVisit || original,
                  original?.selectedDuty,
                );
              }}
              className={`text-[#4361EE] underline font-medium font-poppins text-[13px] ${
                isDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:opacity-80"
              }`}
            >
              {isOpening ? "Loading..." : "View"}
            </button>
          );
        },
      },
    ];
  }, [
    selectedPatient,
    openingReportVisitId,
    dutySlots,
  ]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    setPageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: selectedPageCount, pageIndex: paginationPage },
      autoResetPage: false,
      autoResetSortBy: false,
    },
    useSortBy,
    usePagination,
  );

  const handlePageCountChange = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return;
    localStorage.setItem("visitsPageCount", String(n));
    setSelectedPageCount(n);
    setPageSize(n);
    setPaginationPage(0);
    gotoPage(0);
  };

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil((filteredData?.length || 0) / selectedPageCount) - 1,
    );
    if (paginationPage > maxPage) setPaginationPage(0);
  }, [filteredData?.length, paginationPage, selectedPageCount]);

  useEffect(() => {
    if (pageIndex !== paginationPage) setPaginationPage(pageIndex);
  }, [pageIndex, paginationPage]);

  const handleServiceTabChange = (nextTab) => {
    if (nextTab === SERVICE_TAB_LAB && !hasLabVisits) return;
    setActiveServiceTab(nextTab);
    setPaginationPage(0);
    gotoPage(0);
  };

  const renderServiceTabs = () => (
    <div
      className="mx-auto flex w-full max-w-[420px] items-center gap-1 rounded-xl bg-[#E9EDF5] p-1"
      role="tablist"
      aria-label="Visit service"
    >
      <button
        type="button"
        role="tab"
        aria-selected={activeServiceTab === SERVICE_TAB_HOMECARE}
        aria-label={`Homecare visits (${serviceTabCounts[SERVICE_TAB_HOMECARE]})`}
        onClick={() => handleServiceTabChange(SERVICE_TAB_HOMECARE)}
        className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[10px] px-3 py-2 text-[12px] font-semibold font-poppins transition-all ${
          activeServiceTab === SERVICE_TAB_HOMECARE
            ? "bg-white text-[#3549C7] shadow-[0_3px_10px_rgba(45,55,120,0.12)]"
            : "text-[#686B76] hover:text-[#3549C7]"
        }`}
      >
        <span className="truncate">Homecare</span>
        <span
          className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold ${
            activeServiceTab === SERVICE_TAB_HOMECARE
              ? "bg-[#EEF0FF] text-[#3549C7]"
              : "bg-[#DDE2EC] text-[#686B76]"
          }`}
        >
          {serviceTabCounts[SERVICE_TAB_HOMECARE]}
        </span>
      </button>
      {hasLabVisits && (
        <button
          type="button"
          role="tab"
          aria-selected={activeServiceTab === SERVICE_TAB_LAB}
          aria-label={`Lab visits (${serviceTabCounts[SERVICE_TAB_LAB]})`}
          onClick={() => handleServiceTabChange(SERVICE_TAB_LAB)}
          className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[10px] px-3 py-2 text-[12px] font-semibold font-poppins transition-all ${
            activeServiceTab === SERVICE_TAB_LAB
              ? "bg-white text-[#3549C7] shadow-[0_3px_10px_rgba(45,55,120,0.12)]"
              : "text-[#686B76] hover:text-[#3549C7]"
          }`}
        >
          <span className="truncate">Lab</span>
          <span
            className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold ${
              activeServiceTab === SERVICE_TAB_LAB
                ? "bg-[#EEF0FF] text-[#3549C7]"
                : "bg-[#DDE2EC] text-[#686B76]"
            }`}
          >
            {serviceTabCounts[SERVICE_TAB_LAB]}
          </span>
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div>
        <Loader loading={loading} />
      </div>
    );
  }

  // ✅ Show Assign staff card when Requests context + request item + dutyInfo empty
  const isRequestsContext =
    String(from || "") === "Requests" ||
    selectedFilters.includes("Payment Pending") ||
    selectedFilters.includes("Requests");
  const parentVisitForDetails = selectedPatient?.parentVisit || selectedPatient;
  const isDetailsLabVisit =
    String(parentVisitForDetails?.serviceCategory || "")
      .trim()
      .toLowerCase() === "lab";
  const showAssignStaffCard =
    isRequestsContext &&
    selectedPatient &&
    isRequestsVisit(parentVisitForDetails) &&
    (!Array.isArray(parentVisitForDetails?.dutyInfo) ||
      parentVisitForDetails.dutyInfo.length === 0);

  const dutyStaffName = selectedPatient
    ? getDutyStaffNameFromVisit(parentVisitForDetails)
    : "N/A";
  const standby1Name = selectedPatient
    ? getStandby1NameFromVisit(parentVisitForDetails)
    : "N/A";
  const standby2Name = selectedPatient
    ? getStandby2NameFromVisit(parentVisitForDetails)
    : "N/A";
  //   console.log(selectedPatient);
  return (
    <div className="flex flex-col h-[100vh] overflow-y-auto gap-3 overflow-x-hidden pb-10">
      <input
        ref={labResultInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={handleLabResultFileChange}
      />
      <LabResultPreviewModal
        preview={labResultPreview}
        isUploading={Boolean(labResultUploadingVisitId)}
        onChangeFile={() => chooseLabResultFile(labResultPreview?.visitID)}
        onClose={closeLabResultPreview}
        onUpload={confirmLabResultUpload}
      />
      <div
        className={`flex flex-col min-[1100px]:flex-row items-stretch justify-start w-full min-[1100px]:flex-1 min-[1100px]:min-h-0 h-auto gap-3 ${isReadOnly ? "p-0" : "p-2"} max-w-full`}
      >
        <div className="min-[1100px]:flex-1 flex flex-col gap-3 min-[1100px]:min-h-0 min-w-0 w-full max-w-full h-auto">
          <div className="bg-white rounded-2xl shadow-lg p-2 min-[1100px]:flex-[1.1] min-[1100px]:min-h-0 h-auto flex flex-col justify-between">
            {hasLabVisits && (
              <div className="px-2 pt-2 flex-shrink-0">
                {renderServiceTabs()}
              </div>
            )}
            <div className="flex justify-between items-center p-2 border-b border-gray-100 flex-shrink-0">
              <p className="text-[15px] font-semibold font-poppins whitespace-nowrap">
                Patient List ({filteredData.length})
              </p>

              <div className="flex items-center gap-2">
                {(!isReadOnly || onRefresh) && (
                  <button
                    type="button"
                    onClick={() => refetchVisits()}
                    className="flex items-center justify-center bg-white text-blue-600 border border-blue-600 rounded-md h-[32px] w-[32px] hover:bg-blue-50 transition-colors cursor-pointer shrink-0"
                    style={{ borderWidth: 1.5 }}
                    title="Refresh Data"
                  >
                    <RotateCcw size={16} />
                  </button>
                )}
                <div
                  className="flex items-center bg-white border border-blue-600 rounded-md px-2 h-[32px]"
                  style={{ borderWidth: 1.5 }}
                >
                  <select
                    value={selectedPageCount}
                    onChange={(e) => handlePageCountChange(e.target.value)}
                    className="outline-none font-poppins text-[12px] text-blue-600 bg-transparent font-medium cursor-pointer"
                  >
                    {[25, 50, 75, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-1.5">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 bg-white border border-blue-600 rounded-md px-2.5 py-1.5 text-[12px] font-poppins text-blue-600 outline-none focus:outline-none hover:bg-blue-50 select-none cursor-pointer"
                    >
                      <MdOutlineFilterList size={18} />
                      <span className="font-semibold">
                        {selectedFilters.length === 1
                          ? selectedFilters[0]
                          : "Filters"}
                      </span>
                      {selectedFilters.length > 1 && (
                        <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">
                          {selectedFilters.length}
                        </span>
                      )}
                      <ChevronDown size={14} />
                    </button>

                    {isDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setIsDropdownOpen(false)}
                        />
                        <div className="absolute right-0 mt-1 w-[160px] bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20 font-poppins text-[12px]">
                          {[
                            "All",
                            "Upcoming",
                            "Ongoing",
                            "Payment Pending",
                            "Action Required",
                            "Completed",
                          ].map((filter) => {
                            const isChecked =
                              selectedFilters.includes("All") &&
                              [
                                "All",
                                "Ongoing",
                                "Upcoming",
                                "Payment Pending",
                                "Action Required",
                              ].includes(filter)
                                ? true
                                : selectedFilters.includes(filter);

                            return (
                              <label
                                key={filter}
                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#F6F8FC] cursor-pointer text-gray-700 font-medium select-none"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleFilterChange(filter)}
                                  className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                />
                                <span>{filter}</span>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  {(!selectedFilters.includes("All") ||
                    selectedFilters.length > 1) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFilters(["All"]);
                        setPaginationPage(0);
                      }}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer flex items-center justify-center"
                      title="Reset filters"
                    >
                      <RotateCcw size={16} />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleExportDetailedPdf}
                  disabled={exportingPdf || loading}
                  className="flex items-center gap-1.5 bg-blue-600 text-white border border-blue-600 rounded-md px-2.5 h-[32px] text-[12px] font-poppins font-semibold hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:bg-blue-300 disabled:border-blue-300"
                  style={{ borderWidth: 1.5 }}
                  title="Export accepted, startDuty, endDuty and completed bookings as detailed PDF"
                >
                  {exportingPdf ? "Exporting..." : "Export PDF"}
                </button>
              </div>
            </div>

            {filteredData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[35vh]">
                <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mb-4">
                  <img
                    src={CommonImages.noDuty}
                    className="h-[125px] w-[160px] object-contain"
                    alt="No Duty"
                  />
                </div>
                <h2 className="text-[13px] font-semibold font-poppins text-[#787998]">
                  No visits available
                </h2>
                <p className="text-[11px] text-gray-500 font-poppins mt-1">
                  Try changing the filter.
                </p>
              </div>
            ) : (
              <div className="overflow-auto flex-1 min-h-0 w-full max-w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                <table {...getTableProps()} className="w-full">
                  <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                    {headerGroups.map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        {...headerGroup.getHeaderGroupProps()}
                      >
                        {headerGroup.headers.map((column) => (
                          <th
                            key={column.id}
                            {...column.getHeaderProps(
                              column.getSortByToggleProps(),
                            )}
                            className="px-2 py-1.5 text-left text-[12px] font-medium text-[#8177A8] tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                          >
                            <div className="flex items-center gap-1">
                              {column.render("Header")}
                              {column.canSort && (
                                <SortIcon
                                  isSorted={column.isSorted}
                                  isSortedDesc={column.isSortedDesc}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody
                    {...getTableBodyProps()}
                    className="bg-white divide-y divide-gray-200"
                  >
                    {(() => {
                      const renderRow = (row) => {
                        prepareRow(row);
                        const isSelected =
                          normalizeId(selectedPatient?._id) ===
                          normalizeId(row.original?._id);

                        return (
                          <tr
                            key={row.id}
                            {...row.getRowProps()}
                            onClick={() => handleRowClick(row.original)}
                            className={`cursor-pointer transition-colors ${
                              isSelected
                                ? "bg-[#DFEAFF] border-l-4 border-blue-600 mt-2"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            {row.cells.map((cell) => (
                              <td
                                key={cell.column.id}
                                {...cell.getCellProps()}
                                className="px-2 py-1 text-[12px] text-gray-700 whitespace-nowrap"
                              >
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      };

                      if (
                        selectedFilters.length === 1 &&
                        (selectedFilters.includes("Completed") ||
                          selectedFilters.includes("Action Required"))
                      ) {
                        return tablePage.map(renderRow);
                      }

                      const getDutyCategory = (dutyDate, visitStatus, visit) => {
                        if (
                          visitStatus === "completed" ||
                          visitStatus.includes("completed")
                        ) {
                          return isLabResultPendingVisit(visit)
                            ? "actionRequired"
                            : "completed";
                        }
                        if (!dutyDate) return "month";
                        const mDate = moment(dutyDate).startOf("day");
                        const today = moment().startOf("day");
                        const tomorrow = moment().add(1, "day").startOf("day");
                        const endOfWeek = moment().add(7, "days").endOf("day");

                        if (mDate.isSame(today, "day")) return "today";
                        if (mDate.isSame(tomorrow, "day")) return "tomorrow";
                        if (
                          mDate.isAfter(tomorrow) &&
                          mDate.isSameOrBefore(endOfWeek)
                        )
                          return "week";
                        return "month";
                      };

                      const groups = {
                        today: [],
                        tomorrow: [],
                        week: [],
                        month: [],
                        actionRequired: [],
                        completed: [],
                      };

                      tablePage.forEach((row) => {
                        const visit = row.original?.parentVisit || row.original;
                        const visitStatus = String(visit?.status || "")
                          .trim()
                          .toLowerCase();
                        const cat = getDutyCategory(
                          row.original?.selectedDuty?.date,
                          visitStatus,
                          visit,
                        );
                        groups[cat].push(row);
                      });

                      const CATEGORY_LABELS = {
                        today: "Today's Appointment List",
                        tomorrow: "Tomorrow's Appointment List",
                        week: "Week's Appointment List",
                        month: "Month's Appointment List",
                        actionRequired: "Action Required (Lab Result Pending)",
                        completed: "Completed Appointment List",
                      };

                      const categoriesToShow = [
                        "today",
                        "tomorrow",
                        "week",
                        "month",
                      ];
                      if (
                        selectedFilters.includes("Action Required") ||
                        groups.actionRequired.length > 0
                      ) {
                        categoriesToShow.push("actionRequired");
                      }
                      if (
                        selectedFilters.includes("Completed") ||
                        groups.completed.length > 0
                      ) {
                        categoriesToShow.push("completed");
                      }

                      return categoriesToShow.map((cat) => {
                        const rows = groups[cat];
                        return (
                          <Fragment key={cat}>
                            {/* Group Header Row */}
                            <tr className="bg-[#F8FAFC] select-none border-t border-b border-gray-100">
                              <td
                                colSpan={columns.length}
                                className="px-4 py-2 text-[13px] font-semibold text-gray-700 font-poppins"
                              >
                                <div className="flex items-center gap-2">
                                  <svg
                                    className="w-4 h-4 text-blue-600 shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <rect
                                      x="3"
                                      y="4"
                                      width="18"
                                      height="18"
                                      rx="2"
                                      ry="2"
                                    ></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                  </svg>
                                  <span>
                                    {CATEGORY_LABELS[cat]} ({rows.length})
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {/* Group Rows */}
                            {rows.map(renderRow)}
                          </Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            )}

            {filteredData.length > 0 && (
              <div className="mt-2 mb-1 flex-shrink-0">
                <Page
                  paginationPage={paginationPage}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  canPreviousPage={canPreviousPage}
                  canNextPage={canNextPage}
                  pageOptions={pageOptions}
                  screen="hospital"
                />
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-3 min-[1100px]:flex-[0.9] min-[1100px]:min-h-0 h-auto flex flex-col min-w-0 w-full max-w-full">
            <h3 className="text-[14px] font-semibold font-poppins text-gray-800 mb-3 whitespace-nowrap">
              {showAssignStaffCard ? "Assign staff" : "Duty Details"}
            </h3>

            {!selectedPatient ? (
              <div className="flex-1 flex items-center justify-center text-[12px] text-gray-500 font-poppins">
                No visit selected
              </div>
            ) : showAssignStaffCard ? (
              /* ✅ Requests UI (your screenshot style) */
              <div className="flex-1 overflow-y-auto overflow-x-auto w-full max-w-full">
                <div className="border border-gray-200 rounded-xl overflow-hidden min-w-[500px]">
                  <table className="w-full">
                    <thead className="bg-[#F6F8FC] sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          Service Date
                        </th>
                        <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          Time
                        </th>
                        <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          Duty Staff
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      <tr>
                        <td className="px-4 py-2 text-[12px] font-poppins text-gray-700 whitespace-nowrap">
                          {selectedPatient?.selectedDuty?.date
                            ? moment(selectedPatient.selectedDuty.date).format(
                                "DD/MM/YYYY",
                              )
                            : formatServiceDateRange(
                                selectedPatient?.visitInfo,
                              )}
                        </td>
                        <td className="px-4 py-2 text-[12px] font-poppins text-gray-700 whitespace-nowrap">
                          {getDutyTimeLabel(
                            parentVisitForDetails,
                            selectedPatient?.selectedDuty,
                            dutySlots,
                          )}
                        </td>
                        <td className="px-4 py-2 text-[12px] font-poppins text-gray-700 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <img
                              src={CommonImages.nurseNew}
                              alt="Duty Staff"
                              className="w-6 h-6 rounded-full object-cover"
                            />
                            <span className="text-[#4361EE] font-medium truncate max-w-[220px]">
                              {dutyStaffName}
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-xl p-3">
                    <p className="text-[12px] font-semibold font-poppins text-gray-800 mb-2 whitespace-nowrap">
                      Standby Staff 1
                    </p>
                    <div className="flex items-center gap-2">
                      <img
                        src={CommonImages.nurseNew}
                        alt="Standby 1"
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-[12px] font-poppins text-[#4361EE] font-medium truncate max-w-[220px]">
                        {standby1Name}
                      </span>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl p-3">
                    <p className="text-[12px] font-semibold font-poppins text-gray-800 mb-2 whitespace-nowrap">
                      Standby Staff 2
                    </p>
                    <div className="flex items-center gap-2">
                      <img
                        src={CommonImages.nurseNew}
                        alt="Standby 2"
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-[12px] font-poppins text-[#4361EE] font-medium truncate max-w-[220px]">
                        {standby2Name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* ✅ Normal Duty table (your existing) */
              <div className="flex-1 overflow-x-auto overflow-y-auto w-full max-w-full">
                <table className="w-full min-w-[850px] border border-gray-200 rounded-[2px] overflow-hidden">
                  <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                        Date
                      </th>
                      <th className="px-2 py-1.5 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                        Time
                      </th>
                      <th className="px-2 py-1.5 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                        Assigned staff
                      </th>
                      <th className="px-2 py-1.5 text-center text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                        Check-in / Check-out
                      </th>
                      <th className="px-2 py-1.5 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                        Status
                      </th>
                      <th className="px-2 py-1.5 text-left text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                        Payout
                      </th>
                      {isDetailsLabVisit && (
                        <th className="px-2 py-1.5 text-center text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          Result
                        </th>
                      )}
                      <th className="px-2 py-1.5 text-center text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                        Track
                      </th>
                      {isReadOnly && (
                        <th className="px-2 py-1.5 text-center text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          Action
                        </th>
                      )}
                      {/* <th className="px-3 py-2 text-center text-[13px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                                                Cancell
                                            </th> */}
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedDuties.length === 0 ? (
                      <tr>
                        <td
                          colSpan={
                            7 + (isReadOnly ? 1 : 0) + (isDetailsLabVisit ? 1 : 0)
                          }
                          className="px-3 py-6 text-center text-[12px] text-gray-500 font-poppins"
                        >
                          No duty records available
                        </td>
                      </tr>
                    ) : (
                      sortedDuties.map((duty, index) => {
                        const dutyKey = normalizeId(duty?._id) || String(index);
                        const parentVisit =
                          selectedPatient?.parentVisit || selectedPatient;
                        const isDutySelected =
                          normalizeId(selectedPatient?.selectedDuty?._id) ===
                          dutyKey;
                        const dutyStatus = getDutyStatusLabel(
                          parentVisit,
                          duty,
                        );
                        const canEditDateAndStaff = canEditDutyDateAndStaff(
                          parentVisit,
                          duty,
                        );
                        const canEditTime = canEditDutyTime(parentVisit, duty);

                        const displayTime = getDutyTimeLabel(
                          parentVisit,
                          duty,
                          dutySlots,
                          dutyKey,
                        );

                        const started = !!duty?.startDuty?.verified;
                        const payoutStatus = started
                          ? duty?.payout?.payoutToTheVendor?.status || ""
                          : "";
                        const payoutText = getDutyPayoutText(
                          parentVisit,
                          duty,
                          started,
                        );
                        const canTrack = isTrackingVisibleForDuty(duty);
                        const isLabDutyVisit =
                          String(parentVisit?.serviceCategory || "")
                            .trim()
                            .toLowerCase() === "lab";
                        const dutyRowVisitId = normalizeId(parentVisit?._id);
                        const dutyLabResultUploaded =
                          hasLabResultDocument(parentVisit);
                        const dutyLabVisitCompleted =
                          String(parentVisit?.status || "")
                            .trim()
                            .toLowerCase() === "completed";
                        const canUploadDutyLabResult =
                          !isReadOnly &&
                          dutyLabVisitCompleted &&
                          hasSuccessfullyCollectedLabDuty(parentVisit) &&
                          !dutyLabResultUploaded;
                        const isDutyLabResultUploading =
                          labResultUploadingVisitId === dutyRowVisitId;
                        const isDutyLabResultDisabled =
                          isDutyLabResultUploading ||
                          (!canUploadDutyLabResult && !dutyLabResultUploaded);

                        return (
                          <tr
                            key={dutyKey}
                            className={`hover:bg-[#DFEAFF] transition-colors ${
                              isDutySelected
                                ? "bg-[#DFEAFF] border-l-4 border-blue-600 font-semibold"
                                : ""
                            }`}
                          >
                            <td className="px-2 py-1.5 text-[13px] text-gray-700 font-poppins whitespace-nowrap">
                              {duty.displayDateRange
                                ? duty.displayDateRange
                                : moment(duty.date).format("DD/MM/YYYY")}
                              {!isReadOnly && canEditDateAndStaff && (
                                <button
                                  onClick={(e) =>
                                    openDateModal(e, index, dutyKey)
                                  }
                                  className="ml-2 text-[11px] text-blue-600 px-2 py-[2px] font-poppins"
                                  title="Edit date"
                                >
                                  <img
                                    src={CommonImages.editIconBlue}
                                    className="text-[#4361EE] w-6 h-6"
                                    alt="Edit Date"
                                  />
                                </button>
                              )}
                            </td>

                            <td className="px-2 py-1.5 text-[13px] text-gray-700 font-poppins whitespace-nowrap">
                              <div className="inline-flex items-center gap-2">
                                <span className="font-medium text-gray-800">
                                  {displayTime}
                                </span>

                                {!isReadOnly && canEditTime && (
                                  <button
                                    onClick={() =>
                                      openTimeModal(index, dutyKey, displayTime)
                                    }
                                    className="text-[11px] text-blue-600 px-2 py-[2px] font-poppins"
                                    title="Edit time slot"
                                  >
                                    <img
                                      src={CommonImages.editIconBlue}
                                      className="text-[#4361EE] w-6 h-6 block object-contain"
                                      alt="Edit"
                                    />
                                  </button>
                                )}
                              </div>
                            </td>

                            <td className="px-2 py-1.5 text-[13px] font-poppins whitespace-nowrap">
                              {!isReadOnly && editingDutyIndex === index ? (
                                <div className="flex flex-col items-start gap-1">
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={changingDutyStaff[index] || ""}
                                      onChange={(e) => {
                                        const staffID = e.target.value;
                                        setChangingDutyStaff((prev) => ({
                                          ...prev,
                                          [index]: staffID,
                                        }));
                                        if (staffID) {
                                          handleDutyStaffChange(index, staffID);
                                        }
                                      }}
                                      autoFocus
                                      disabled={
                                        availableDutyStaffLoading ||
                                        availableDutyStaffSaving ||
                                        availableDutyStaff.length === 0
                                      }
                                      className="bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins border border-gray-300 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                      <option value="">
                                        {availableDutyStaffLoading
                                          ? "Loading staff..."
                                          : availableDutyStaffSaving
                                            ? "Updating staff..."
                                            : availableDutyStaffError
                                              ? "Unable to load staff"
                                              : availableDutyStaff.length === 0
                                                ? "No available staff"
                                                : "Select staff"}
                                      </option>
                                      {availableDutyStaff.map((staff) => (
                                        <option key={staff.id} value={staff.id}>
                                          {staff.name}
                                        </option>
                                      ))}
                                    </select>

                                    <button
                                      onClick={() => handleCloseDropdown(index)}
                                      disabled={availableDutyStaffSaving}
                                      className="p-1 rounded-full hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                                      title="Close"
                                    >
                                      <X size={14} className="text-gray-700" />
                                    </button>
                                  </div>

                                  {availableDutyStaffError && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openDutyStaffEditor(index, dutyKey)
                                      }
                                      className="text-[11px] text-red-600 hover:underline"
                                    >
                                      {availableDutyStaffError}. Retry
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-[#4361EE] font-medium">
                                    {getDutyHcpName(selectedPatient, duty)}
                                  </span>

                                  {!isReadOnly && canEditDateAndStaff && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openDutyStaffEditor(index, dutyKey)
                                      }
                                      className="p-1 rounded-full hover:bg-blue-50"
                                      title="Change staff"
                                    >
                                      <Pencil
                                        size={14}
                                        className="text-[#4361EE]"
                                      />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>

                            <td className="px-2 text-center py-1.5 text-[13px] text-gray-700 font-poppins whitespace-nowrap">
                              {duty?.startDuty?.verified
                                ? formatCheckTime(duty?.startDuty?.time)
                                : "-"}
                              {" / "}
                              {duty?.endDuty?.verified
                                ? formatCheckTime(duty?.endDuty?.time)
                                : "-"}
                            </td>

                            <td className="px-2 py-1.5 text-[13px] font-poppins whitespace-nowrap">
                              <span className="text-gray-700">
                                {dutyStatus}
                              </span>
                            </td>

                            <td className="px-2 py-1.5 text-[13px] font-poppins whitespace-nowrap">
                              <span
                                className={`text-[12px] font-medium ${getPayoutColorClass(payoutStatus)}`}
                              >
                                {payoutText}
                              </span>
                            </td>

                            {isLabDutyVisit && (
                              <td className="px-2 py-1.5 text-center text-[12px] font-poppins whitespace-nowrap">
                                <div className="flex min-w-[118px] flex-col items-center gap-1">
                                  <span
                                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                      isDutyLabResultUploading
                                        ? "bg-blue-50 text-blue-700"
                                        : dutyLabResultUploaded
                                          ? "bg-emerald-50 text-emerald-700"
                                          : "bg-amber-50 text-amber-700"
                                    }`}
                                  >
                                    <span
                                      aria-hidden="true"
                                      className={`h-1.5 w-1.5 rounded-full ${
                                        isDutyLabResultUploading
                                          ? "bg-blue-500 animate-pulse"
                                          : dutyLabResultUploaded
                                            ? "bg-emerald-500"
                                            : "bg-amber-500"
                                      }`}
                                    />
                                    {isDutyLabResultUploading
                                      ? "Uploading"
                                      : dutyLabResultUploaded
                                        ? "Uploaded"
                                        : "Not uploaded"}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={isDutyLabResultDisabled}
                                    aria-label={
                                      dutyLabResultUploaded
                                        ? "View uploaded lab result"
                                        : "Upload lab result PDF"
                                    }
                                    title={
                                      isDutyLabResultDisabled &&
                                      !isDutyLabResultUploading
                                        ? "Upload is available after the visit and sample collection are completed."
                                        : undefined
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (dutyLabResultUploaded) {
                                        openLabResult(dutyRowVisitId);
                                      } else if (canUploadDutyLabResult) {
                                        chooseLabResultFile(dutyRowVisitId);
                                      }
                                    }}
                                    className={`font-semibold text-[11px] underline underline-offset-2 ${
                                      isDutyLabResultDisabled
                                        ? "text-gray-400 cursor-not-allowed"
                                        : "text-[#4361EE] hover:opacity-80"
                                    }`}
                                  >
                                    {isDutyLabResultUploading
                                      ? "Please wait..."
                                      : dutyLabResultUploaded
                                        ? "View"
                                        : canUploadDutyLabResult
                                          ? "Upload PDF"
                                          : "Upload unavailable"}
                                  </button>
                                </div>
                              </td>
                            )}

                            <td className="px-2 py-1.5 text-center text-[12px] font-poppins whitespace-nowrap">
                              {canTrack ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLiveTracking(selectedPatient, duty);
                                  }}
                                  className="px-2 py-1 bg-green-100 text-green-700 text-[10px] rounded-md hover:bg-green-200 transition-colors inline-flex items-center gap-1 font-poppins whitespace-nowrap"
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 111.314 0z"
                                    />
                                  </svg>
                                  Track
                                </button>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            {isReadOnly && (
                              <td className="px-2 py-1.5 text-center text-[13px] font-poppins whitespace-nowrap">
                                {(() => {
                                  let isCrossed = false;
                                  const dutyDate = duty?.date
                                    ? moment(
                                        duty.date?.$date || duty.date,
                                      ).startOf("day")
                                    : null;
                                  const today = moment().startOf("day");

                                  if (dutyDate && dutyDate.isBefore(today)) {
                                    isCrossed = true;
                                  } else {
                                    const dutyEndTime =
                                      duty?.endTime?.$date || duty?.endTime;

                                    if (
                                      dutyEndTime &&
                                      String(dutyEndTime).trim() !== "" &&
                                      typeof dutyEndTime !== "object"
                                    ) {
                                      isCrossed = moment().isAfter(
                                        moment(dutyEndTime),
                                      );
                                    } else if (
                                      dutyDate &&
                                      dutyDate.isSame(today) &&
                                      duty?.slot
                                    ) {
                                      const parts = duty.slot.split(" to ");
                                      const to =
                                        parts.length > 1
                                          ? parts[1].trim()
                                          : null;
                                      if (to) {
                                        const endMoment = moment(
                                          `${dutyDate.format("YYYY-MM-DD")} ${to}`,
                                          "YYYY-MM-DD hh:mm A",
                                        );
                                        isCrossed = moment().isAfter(endMoment);
                                      }
                                    }
                                  }

                                  const notCompleted =
                                    String(
                                      duty?.serviceStatus || "",
                                    ).toLowerCase() !== "completed";

                                  const isPaymentPending =
                                    String(
                                      parentVisit?.status || "",
                                    ).toLowerCase() === "payment pending" ||
                                    getVisitStatusLabel(parentVisit) ===
                                      "Payment pending";

                                  if (
                                    isCrossed &&
                                    notCompleted &&
                                    isReadOnly &&
                                    !isPaymentPending
                                  ) {
                                    return (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenDutyCloseModal(
                                            index,
                                            dutyKey,
                                          );
                                        }}
                                        className="px-2 py-1 bg-red-100 text-red-600 text-[11px] font-medium rounded hover:bg-red-200 transition-colors"
                                      >
                                        Close
                                      </button>
                                    );
                                  }
                                  return (
                                    <span className="text-gray-400">-</span>
                                  );
                                })()}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="w-full min-[1100px]:w-[340px] lg:w-[380px] flex-shrink-0 min-w-0 overflow-hidden min-[1100px]:h-full h-auto">
          <PatientHistory
            selectedPatient={selectedPatient?.parentVisit || selectedPatient}
            dischargeSummaries={dischargeSummariesForHistory}
            showDutyType={true}
          />
        </div>
      </div>

      {timeModalOpen && !isReadOnly && selectedPatient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-semibold font-poppins text-gray-800">
                Select Time Slot
              </h3>
              <button
                onClick={closeTimeModal}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <X size={18} className="text-gray-700" />
              </button>
            </div>

            {dutyForModal && (
              <p className="text-[11px] text-gray-500 mb-3 font-poppins">
                {dutyForModal.displayDateRange
                  ? dutyForModal.displayDateRange
                  : moment(dutyForModal.date).format("DD/MM/YYYY - dddd")}
              </p>
            )}

            <div className="max-h-[260px] overflow-y-auto mb-4">
              {dynamicSlotsLoading ? (
                <div className="text-center py-6 text-gray-500 font-poppins text-[12px]">
                  Loading slots...
                </div>
              ) : dynamicSlotsError ? (
                <div className="text-center py-6 text-red-500 font-poppins text-[12px]">
                  {dynamicSlotsError}
                </div>
              ) : dynamicSlots.length === 0 ? (
                <div className="text-center py-6 text-gray-500 font-poppins text-[12px]">
                  No slots available for this date.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {dynamicSlots.map((slot) => {
                    const active = timeModalSelectedSlot === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setTimeModalSelectedSlot(slot)}
                        className={`px-3 py-2 rounded-lg text-[12px] font-poppins border text-center transition-colors ${
                          active
                            ? "bg-[#4951EC] border-[#4951EC] text-white"
                            : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"
                        }`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeTimeModal}
                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applySelectedTimeSlot}
                disabled={!timeModalSelectedSlot || dynamicSlotsLoading}
                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-[#4951EC] text-white hover:bg-[#3d3acc] disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {dateModalOpen && !isReadOnly && selectedPatient && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-semibold font-poppins text-gray-800">
                Change Duty Date
              </h3>
              <button
                onClick={closeDateModal}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <X size={18} className="text-gray-700" />
              </button>
            </div>

            {dateModalDutyIndex != null &&
              sortedDuties?.[dateModalDutyIndex] && (
                <div className="mb-4">
                  <p className="text-[11px] text-gray-500 font-poppins mb-1">
                    Current Date:{" "}
                    {moment(sortedDuties[dateModalDutyIndex].date).format(
                      "DD/MM/YYYY - dddd",
                    )}
                  </p>
                  <p className="text-[11px] text-blue-600 font-poppins font-medium">
                    Rescheduling available on/after:{" "}
                    {moment(dateModalMinDate).format("DD/MM/YYYY")}
                  </p>
                </div>
              )}

            <div className="mb-5">
              <label className="block text-[12px] font-medium text-gray-700 font-poppins mb-2">
                Select New Date
              </label>
              <DatePicker
                selected={dateModalSelectedDate}
                onChange={(date) => setDateModalSelectedDate(date)}
                minDate={dateModalMinDate ? new Date(dateModalMinDate) : null}
                excludeDates={sortedDuties
                  .filter((d, idx) => idx !== dateModalDutyIndex && d?.date)
                  .map((d) => moment(d.date).toDate())}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select a date"
                className="w-full bg-[#F6F8FC] rounded-md px-3 py-2 text-[13px] font-poppins border border-gray-300 outline-none focus:border-[#4951EC]"
                calendarClassName="font-poppins text-[12px]"
                inline
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDateModal}
                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applySelectedDutyDate}
                disabled={!dateModalSelectedDate}
                className="px-3 py-1.5 rounded-md text-[12px] font-poppins bg-[#4951EC] text-white hover:bg-[#3d3acc] disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VISIT CANCELLATION MODAL */}
      {cancelModalOpen && !isReadOnly && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-auto overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
              <div>
                <h3 className="text-[16px] font-semibold font-poppins text-gray-900">
                  Visit Cancellation
                </h3>
                <p className="text-[11px] font-poppins text-gray-500 mt-0.5">
                  {selectedPatient?.patient?.name ||
                    selectedPatient?.patientInfo?.personalInfo?.basicInfo
                      ?.nameInfo?.fullName ||
                    "Patient"}
                  {" • "}
                  {cancelModalDutyIndex != null &&
                  sortedDuties?.[cancelModalDutyIndex]
                    ? moment(sortedDuties[cancelModalDutyIndex].date).format(
                        "DD MM YYYY",
                      )
                    : ""}
                </p>
              </div>
              <button
                onClick={closeCancelModal}
                className="p-1.5 rounded-full hover:bg-red-100 transition-colors"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Sub Title */}
              {/* <div>
                                <label className="block text-[12px] font-semibold font-poppins text-gray-700 mb-1">
                                    Sub Title
                                </label>
                                <input
                                    type="text"
                                    value={cancelFormSubTitle}
                                    onChange={(e) => setCancelFormSubTitle(e.target.value)}
                                    placeholder="Enter a brief sub title"
                                    className="w-full bg-[#F6F8FC] rounded-lg px-3 py-2 text-[13px] font-poppins border border-gray-200 outline-none focus:border-[#4951EC] focus:ring-1 focus:ring-[#4951EC]/20 transition-all"
                                />
                            </div> */}

              {/* Cancel Date */}
              {/* <div>
                                <label className="block text-[12px] font-semibold font-poppins text-gray-700 mb-1">
                                    Cancel Date
                                </label>
                                <input
                                    type="date"
                                    value={cancelFormDate}
                                    onChange={(e) => setCancelFormDate(e.target.value)}
                                    className="w-full bg-[#F6F8FC] rounded-lg px-3 py-2 text-[13px] font-poppins border border-gray-200 outline-none focus:border-[#4951EC] focus:ring-1 focus:ring-[#4951EC]/20 transition-all"
                                />
                            </div> */}

              {/* Reason */}
              <div>
                <label className="block text-[12px] font-semibold font-poppins text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={cancelFormReason}
                    onChange={(e) => setCancelFormReason(e.target.value)}
                    className="w-full appearance-none bg-[#F6F8FC] rounded-md px-3 py-2 pr-8 text-[13px] font-poppins border border-gray-200 outline-none focus:border-[#4951EC] focus:ring-1 focus:ring-[#4951EC]/20 transition-all cursor-pointer"
                  >
                    <option value="" disabled>
                      Select a reason
                    </option>
                    {notAvailableReasons?.length > 0 &&
                      notAvailableReasons.map((reason, i) => (
                        <option key={i} value={reason}>
                          {reason}
                        </option>
                      ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown size={14} className="text-gray-500" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-[12px] font-semibold font-poppins text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={cancelFormDescription}
                  onChange={(e) => setCancelFormDescription(e.target.value)}
                  placeholder="Add any additional details..."
                  rows={3}
                  className="w-full bg-[#F6F8FC] rounded-md px-3 py-2 text-[13px] font-poppins border border-gray-200 outline-none focus:border-[#4951EC] focus:ring-1 focus:ring-[#4951EC]/20 transition-all resize-none"
                />
              </div>

              {/* Authorizer Name */}
              <div>
                <label className="block text-[12px] font-semibold font-poppins text-gray-700 mb-1">
                  Approved By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={cancelFormAuthorizerName}
                  onChange={(e) => setCancelFormAuthorizerName(e.target.value)}
                  placeholder="Enter authorizer name"
                  className="w-full bg-[#F6F8FC] rounded-md px-3 py-2 text-[13px] font-poppins border border-gray-200 outline-none focus:border-[#4951EC] focus:ring-1 focus:ring-[#4951EC]/20 transition-all"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={closeCancelModal}
                className="px-4 py-2 rounded-lg text-[12px] font-poppins font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={submitCancelDuty}
                disabled={!cancelFormReason || !cancelFormAuthorizerName.trim()}
                className="px-5 py-2 rounded-lg text-[12px] font-poppins font-semibold bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Submit Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REPORTS MODAL (your original code) */}
      {reportsModalOpen && reportVisit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
          <div className="bg-white w-[95vw] max-w-4xl rounded-2xl shadow-2xl border overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between border-b bg-white">
              <div className="min-w-0">
                <h3 className="text-[14px] font-semibold font-poppins text-gray-900">
                  Reports
                </h3>
                <p className="text-[10px] text-gray-500 font-poppins mt-0.5 truncate">
                  {reportVisit?.jobID ? `Job ID: ${reportVisit.jobID} • ` : ""}
                  {reportVisit?.patient?.name || "Patient"}
                </p>
              </div>

              <button
                onClick={closeAllReportsModal}
                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                title="Close"
              >
                <X size={18} className="text-gray-700" />
              </button>
            </div>

            <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
              <div className="rounded-2xl border border-blue-100 overflow-hidden bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="px-4 py-3 border-b sm:border-b-0 sm:border-r border-blue-50">
                    <div className="flex items-center gap-2 text-[10px] text-gray-500 font-poppins">
                      <IoMdBed className="text-[#3E57E8]" size={18} />
                      Patient
                    </div>
                    <div className="text-[12px] text-gray-900 font-semibold font-poppins mt-1 truncate">
                      {reportVisit?.patient?.name || "Patient"}
                    </div>
                  </div>

                  <div className="px-4 py-3">
                    <div className="flex items-center gap-2 text-[10px] text-gray-500 font-poppins">
                      <FaUserNurse className="text-[#3E57E8]" size={14} />
                      Staff
                    </div>
                    <div className="text-[12px] text-gray-900 font-semibold font-poppins mt-1 truncate">
                      {reportVisit?.specialistInfo?.name ||
                        reportVisit?.dutyStaff?.name ||
                        "N/A"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Separate UI for Assessment */}
              {reportVisit?.isAssessment === true &&
                completedReportAssessments &&
                completedReportAssessments.length > 0 && (
                  <div className="mt-5 space-y-4">
                    {completedReportAssessments.map((assess, idx) => {
                      const assessKey = assess?._id || String(idx);
                      const isOpen = !!openAssessments[idx];
                      const reportTitle =
                        completedReportAssessments.length > 1
                          ? `Assessment  ${idx + 1}`
                          : "Assessment ";

                      return (
                        <div
                          key={assessKey}
                          className="rounded-2xl border-2 border-emerald-500 overflow-hidden bg-white shadow-sm"
                        >
                          <div className="bg-emerald-50 px-6 py-4 flex items-center justify-between border-b border-emerald-200">
                            <div>
                              <h4 className="text-[14px] font-bold font-poppins text-emerald-950">
                                {reportTitle}
                              </h4>
                              <p className="text-[10px] text-emerald-700 font-poppins mt-0.5">
                                Assessed by:{" "}
                                {reportVisit?.assessmentStaff?.name ||
                                  assess?.assessmentStaff?.name ||
                                  "N/A"}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setOpenAssessments((prev) => ({
                                  ...prev,
                                  [idx]: !prev[idx],
                                }))
                              }
                              className="shrink-0 flex items-center gap-2 text-emerald-700 hover:text-emerald-800 font-semibold"
                            >
                              <span className="text-[11px] font-poppins">
                                {isOpen ? "Hide" : "Show"}
                              </span>
                              {isOpen ? (
                                <ChevronDown size={18} />
                              ) : (
                                <ChevronRight size={18} />
                              )}
                            </button>
                          </div>

                          {isOpen ? (
                            <div className="p-6 space-y-6">
                              {/* Session Details */}
                              {assess.treatmentPlan && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100 text-[12px] font-poppins">
                                  <div>
                                    <span className="text-gray-500 font-medium block">
                                      Assessment date
                                    </span>
                                    <span className="text-gray-900 font-semibold mt-1 block">
                                      {assess.treatmentPlan.requestedDate
                                        ? moment(
                                            assess.treatmentPlan.requestedDate,
                                          ).format("DD MM YYYY")
                                        : "-"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 font-medium block">
                                      Session Frequency
                                    </span>
                                    <span className="text-gray-900 font-semibold mt-1 block">
                                      {assess.treatmentPlan.sessionFrequency ||
                                        "-"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 font-medium block">
                                      Timing Slot
                                    </span>
                                    <span className="text-gray-900 font-semibold mt-1 block">
                                      {assess.treatmentPlan.sessionStartTime &&
                                      assess.treatmentPlan.sessionEndTime
                                        ? `${moment(assess.treatmentPlan.sessionStartTime).format("hh:mm A")} - ${moment(assess.treatmentPlan.sessionEndTime).format("hh:mm A")}`
                                        : "-"}
                                    </span>
                                  </div>
                                </div>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Complaints Section */}
                                {assess.complaints && (
                                  <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                    <h5 className="text-[13px] font-bold text-gray-900 border-b pb-2 flex items-center gap-1.5">
                                      <span className="w-1.5 h-3 bg-emerald-500 rounded-full" />
                                      Patient Complaints
                                    </h5>
                                    <div className="grid grid-cols-1 gap-2 text-[12px]">
                                      <div className="flex">
                                        <span className="text-gray-500 w-28 shrink-0 font-medium">
                                          Complaint:
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          {assess.complaints.complaint || "-"}
                                        </span>
                                      </div>
                                      <div className="flex">
                                        <span className="text-gray-500 w-28 shrink-0 font-medium">
                                          Duration:
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          {assess.complaints.duration || "-"}
                                        </span>
                                      </div>
                                      <div className="flex">
                                        <span className="text-gray-500 w-28 shrink-0 font-medium">
                                          Location:
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          {assess.complaints.location || "-"}
                                        </span>
                                      </div>
                                      <div className="flex">
                                        <span className="text-gray-500 w-28 shrink-0 font-medium">
                                          Limitations:
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          {assess.complaints
                                            .functionalLimitations || "-"}
                                        </span>
                                      </div>
                                      <div className="flex">
                                        <span className="text-gray-500 w-28 shrink-0 font-medium">
                                          Prev Treatment:
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          {assess.complaints
                                            .previousTreatment || "-"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Observation Section */}
                                {assess.onObservation && (
                                  <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                    <h5 className="text-[13px] font-bold text-gray-900 border-b pb-2 flex items-center gap-1.5">
                                      <span className="w-1.5 h-3 bg-emerald-500 rounded-full" />
                                      Observations
                                    </h5>
                                    <div className="grid grid-cols-1 gap-3 text-[12px]">
                                      <div className="flex">
                                        <span className="text-gray-500 w-28 shrink-0 font-medium">
                                          Body Built:
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          {assess.onObservation.bodyBuilt ||
                                            "-"}
                                        </span>
                                      </div>
                                      <div className="flex">
                                        <span className="text-gray-500 w-28 shrink-0 font-medium">
                                          Posture:
                                        </span>
                                        <span className="text-gray-900 font-semibold">
                                          {assess.onObservation.posture || "-"}
                                        </span>
                                      </div>
                                      {assess.onObservation.deformities &&
                                        assess.onObservation.deformities
                                          .length > 0 && (
                                          <div className="flex flex-col gap-1">
                                            <span className="text-gray-500 font-medium">
                                              Deformities:
                                            </span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {assess.onObservation.deformities.map(
                                                (item, i) => (
                                                  <span
                                                    key={i}
                                                    className="px-2.5 py-1 bg-red-50 text-red-700 text-[10px] font-medium rounded-md border border-red-100"
                                                  >
                                                    {item}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      {assess.onObservation.muscleWastes &&
                                        assess.onObservation.muscleWastes
                                          .length > 0 && (
                                          <div className="flex flex-col gap-1">
                                            <span className="text-gray-500 font-medium">
                                              Muscle Wastes:
                                            </span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {assess.onObservation.muscleWastes.map(
                                                (item, i) => (
                                                  <span
                                                    key={i}
                                                    className="px-2.5 py-1 bg-orange-50 text-orange-700 text-[10px] font-medium rounded-md border border-orange-100"
                                                  >
                                                    {item}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      {assess.onObservation.pressureSore &&
                                        assess.onObservation.pressureSore
                                          .length > 0 && (
                                          <div className="flex flex-col gap-1">
                                            <span className="text-gray-500 font-medium">
                                              Pressure Sore:
                                            </span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {assess.onObservation.pressureSore.map(
                                                (item, i) => (
                                                  <span
                                                    key={i}
                                                    className="px-2.5 py-1 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-md border border-amber-100"
                                                  >
                                                    {item}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      {assess.onObservation.swelling &&
                                        assess.onObservation.swelling.length >
                                          0 && (
                                          <div className="flex flex-col gap-1">
                                            <span className="text-gray-500 font-medium">
                                              Swelling:
                                            </span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {assess.onObservation.swelling.map(
                                                (item, i) => (
                                                  <span
                                                    key={i}
                                                    className="px-2.5 py-1 bg-blue-50 text-blue-700 text-[10px] font-medium rounded-md border border-blue-100"
                                                  >
                                                    {item}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Treatment Plan Section */}
                              {assess.treatmentPlan &&
                                assess.treatmentPlan.sessionDurationDays
                                  ?.length > 0 && (
                                  <div className="border border-gray-200 rounded-xl p-4 space-y-4">
                                    <div className="flex items-center justify-between border-b pb-2">
                                      <h5 className="text-[13px] font-bold text-gray-900 flex items-center gap-1.5">
                                        <span className="w-1.5 h-3 bg-emerald-500 rounded-full" />
                                        Treatment Plan & Objectives
                                      </h5>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setIsTreatmentPlanOpen(
                                            !isTreatmentPlanOpen,
                                          )
                                        }
                                        className="shrink-0 flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 font-semibold"
                                      >
                                        <span>
                                          {isTreatmentPlanOpen
                                            ? "Hide"
                                            : "Show"}
                                        </span>
                                        {isTreatmentPlanOpen ? (
                                          <ChevronDown size={14} />
                                        ) : (
                                          <ChevronRight size={14} />
                                        )}
                                      </button>
                                    </div>

                                    {isTreatmentPlanOpen && (
                                      <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px]">
                                          <div>
                                            <span className="text-gray-500 font-medium block">
                                              Target Area:
                                            </span>
                                            <span className="text-gray-900 font-semibold mt-1 block">
                                              {assess.treatmentPlan
                                                .targetArea || "-"}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-500 font-medium block">
                                              Treatment Types:
                                            </span>
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                              {assess.treatmentPlan
                                                .treatmentType &&
                                              assess.treatmentPlan.treatmentType
                                                .length > 0 ? (
                                                assess.treatmentPlan.treatmentType.map(
                                                  (item, i) => (
                                                    <span
                                                      key={i}
                                                      className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-semibold rounded-md border border-emerald-100"
                                                    >
                                                      {item}
                                                    </span>
                                                  ),
                                                )
                                              ) : (
                                                <span className="text-gray-900 font-semibold">
                                                  -
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-[12px] space-y-2">
                                          <div>
                                            <span className="text-gray-500 font-medium block">
                                              Treatment Goals:
                                            </span>
                                            <span className="text-gray-900 font-medium mt-1 block bg-gray-50 p-2.5 rounded-lg border border-gray-100 whitespace-pre-line text-[11px] leading-relaxed">
                                              {assess.treatmentPlan
                                                .treatmentGoals || "-"}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-500 font-medium block">
                                              Instructions for Patient:
                                            </span>
                                            <span className="text-gray-900 font-medium mt-1 block bg-gray-50 p-2.5 rounded-lg border border-gray-100 whitespace-pre-line text-[11px] leading-relaxed">
                                              {assess.treatmentPlan
                                                .instructionForPatient || "-"}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-500 font-medium block">
                                              Session Duration:
                                            </span>
                                            <span className="text-gray-900 font-medium mt-1 block bg-gray-50 p-2.5 rounded-lg border border-gray-100 whitespace-pre-line text-[11px] leading-relaxed">
                                              {assess.treatmentPlan
                                                .sessionFrequency || "-"}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-500 font-medium block">
                                              Session Date:
                                            </span>
                                            <span className="text-gray-900 font-medium mt-1 block bg-gray-50 p-2.5 rounded-lg border border-gray-100 whitespace-pre-line text-[11px] leading-relaxed">
                                              {(() => {
                                                const start =
                                                  assess?.treatmentPlan
                                                    ?.sessionStartTime;
                                                const end =
                                                  assess?.treatmentPlan
                                                    ?.sessionEndTime;

                                                if (!start && !end) return "-";

                                                const startFormatted =
                                                  moment(start).format(
                                                    "DD-MM-YYYY",
                                                  );
                                                const endFormatted =
                                                  moment(end).format(
                                                    "DD-MM-YYYY",
                                                  );

                                                // If same date, show once
                                                if (
                                                  startFormatted ===
                                                  endFormatted
                                                ) {
                                                  return startFormatted;
                                                }

                                                // Otherwise show start to end
                                                return `${startFormatted} to ${endFormatted}`;
                                              })()}
                                            </span>
                                          </div>

                                          <div>
                                            <span className="text-gray-500 font-medium block">
                                              Session Time:
                                            </span>
                                            <span className="text-gray-900 font-medium mt-1 block bg-gray-50 p-2.5 rounded-lg border border-gray-100 whitespace-pre-line text-[11px] leading-relaxed">
                                              {moment(
                                                assess.treatmentPlan
                                                  .sessionStartTime,
                                              ).format("hh:mm A") +
                                                " - " +
                                                moment(
                                                  assess.treatmentPlan
                                                    .sessionEndTime,
                                                ).format("hh:mm A") || "-"}
                                            </span>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                            </div>
                          ) : (
                            <div className="">
                              {/* <button
                                                            type="button"
                                                            onClick={() => setOpenAssessments(prev => ({ ...prev, [idx]: true }))}
                                                            className="text-[13px] text-emerald-700 hover:text-emerald-800 font-medium hover:underline font-poppins"
                                                        >
                                                            Click to view assessment report details
                                                        </button> */}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

              {reportVisit?.isAssessment === true &&
                (!completedReportAssessments ||
                  completedReportAssessments.length === 0) && (
                  <div className="mt-5 rounded-xl border border-dashed p-6 text-center text-[11px] text-gray-500 font-poppins">
                    No assessment reports available yet.
                  </div>
                )}

              <div className="mt-5 space-y-4">
                {sortedReportDuties.length === 0 ? (
                  <div className="rounded-xl border border-dashed p-6 text-center text-[11px] text-gray-500 font-poppins">
                    No duty records available.
                  </div>
                ) : (
                  sortedReportDuties.map((duty, idx) => {
                    const dutyKey = normalizeId(duty?._id) || String(idx);
                    const isOpen = !!expandedDays?.[dutyKey];

                    const timingLabel = getDutyTimeLabel(
                      reportVisit,
                      duty,
                      dutySlots,
                      dutyKey,
                    );

                    const services = Array.isArray(duty?.services)
                      ? duty.services
                      : [];
                    const sortedServices = [...services].sort((a, b) => {
                      const av = a?.dateTime ? moment(a.dateTime).valueOf() : 0;
                      const bv = b?.dateTime ? moment(b.dateTime).valueOf() : 0;
                      return av - bv;
                    });
                    // Lab catalogue items belong in Booking Information, not the report timeline.
                    const reportServices = isExplicitLabReport ? [] : sortedServices;

                    return (
                      <div
                        key={dutyKey}
                        className="rounded-2xl border-2 border-[#3E57E8] overflow-hidden bg-white"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-12">
                          <div className="sm:col-span-4 border-b sm:border-b-0 sm:border-r border-blue-200 p-4 text-center">
                            <div className="text-[13px] font-semibold font-poppins text-gray-900">
                              {duty.displayDateRange
                                ? duty.displayDateRange
                                : moment(duty.date).format("DD MM YYYY")}
                            </div>
                            <div className="text-[11px] font-poppins text-gray-500 mt-1">
                              {duty.displayDateRange
                                ? "Date Range"
                                : `Day ${duty?.day ?? idx + 1}`}
                            </div>
                          </div>

                          <div className="sm:col-span-8 p-4">
                            <button
                              type="button"
                              onClick={() => toggleDay(dutyKey)}
                              className="w-full flex items-center justify-between gap-3"
                            >
                              <div className="min-w-0 text-left">
                                <div className="text-[14px] font-semibold font-poppins text-[#3E57E8]">
                                  Reports
                                </div>
                                <div className="text-[11px] font-poppins text-gray-600 mt-1 truncate">
                                  Timing: {toDashTiming(timingLabel)}
                                </div>
                              </div>

                              <div className="shrink-0 flex items-center gap-2 text-[#3E57E8]">
                                <span className="text-[11px] font-poppins">
                                  {isOpen ? "Hide" : "Show"}
                                </span>
                                {isOpen ? (
                                  <ChevronDown size={18} />
                                ) : (
                                  <ChevronRight size={18} />
                                )}
                              </div>
                            </button>
                          </div>
                        </div>

                        {isOpen && (
                          <div className="border-t border-blue-200 p-4">
                            {isReportVisitLab && (
                              <>
                                <LabCollectionReport
                                  collection={duty?.labCollection}
                                />
                                {!isReadOnly &&
                                  isExplicitLabReport &&
                                  duty?.startDuty?.verified === true &&
                                  duty?.endDuty?.verified !== true &&
                                  !["collected", "failed"].includes(
                                    String(duty?.labCollection?.status || "")
                                      .trim()
                                      .toLowerCase(),
                                  ) && (
                                    <div className="mt-3 flex justify-end">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setLabFailureTarget({
                                            visitID: normalizeId(reportVisit?._id),
                                            dutyID: normalizeId(duty?._id),
                                          });
                                          setLabFailureReason("");
                                        }}
                                        className="rounded-lg border border-red-300 bg-white px-4 py-2 text-[11px] font-semibold font-poppins text-red-700 hover:bg-red-50"
                                      >
                                        Close collection as failed
                                      </button>
                                    </div>
                                  )}
                              </>
                            )}

                            {reportServices.length === 0 ? (
                              !isReportVisitLab && (
                                <div className="rounded-xl border border-dashed p-6 text-center text-[11px] text-gray-500 font-poppins">
                                  No report available for this day.
                                </div>
                              )
                            ) : (
                              <div
                                className={`relative ${
                                  isReportVisitLab ? "mt-4" : ""
                                }`}
                              >
                                <div className="absolute left-[18px] top-2 bottom-2 w-[3px] bg-emerald-500/70 rounded-full" />

                                <div className="space-y-10">
                                  {reportServices.map((srv, sidx) => {
                                    const id =
                                      normalizeId(srv?._id) || String(sidx);
                                    const timeOnly = srv?.dateTime
                                      ? moment(srv.dateTime).format("hh:mm A")
                                      : "-";
                                    const rawValues = srv?.values;
                                    const values = asObject(rawValues);

                                    const title =
                                      srv?.name ||
                                      srv?.serviceName ||
                                      `Service ${sidx + 1}`;
                                    const serviceNameKey = String(title || "")
                                      .trim()
                                      .toLowerCase();
                                    const role = String(
                                      reportVisit?.specialistInfo?.role || "",
                                    )
                                      .trim()
                                      .toLowerCase();
                                    const isPhysiotherapist =
                                      role === "physiotherapist";
                                    const checklistValues = asArray(rawValues)
                                      .map((v) => String(v ?? "").trim())
                                      .filter(Boolean);

                                    const servicesProvided =
                                      getServiceProvided(values);
                                    const improvements =
                                      getImprovements(values);
                                    const serviceProvidedDetails =
                                      getServiceProvidedDetails(values);
                                    const explicitServiceName =
                                      getServiceNameField(values);
                                    const note = getNote(values, srv);
                                    const painScaleText = getPainScaleText(
                                      srv,
                                      rawValues,
                                    );
                                    const additionalServiceName =
                                      pickFromValues(values, [
                                        "name",
                                        "serviceName",
                                        "service",
                                        "Service Name",
                                      ]);
                                    const additionalProvidedDetail =
                                      pickFromValues(values, [
                                        "providedDetail",
                                        "providedDetails",
                                        "serviceProvidedDetails",
                                        "Service Provided Details",
                                      ]);

                                    const medicationDoseRows = (() => {
                                      if (
                                        Array.isArray(values?.doseProvidedTime)
                                      ) {
                                        return values.doseProvidedTime
                                          .map((row) => ({
                                            value: String(
                                              row?.value ?? "",
                                            ).trim(),
                                            time: row?.dateTime
                                              ? moment(row.dateTime).format(
                                                  "h:mm A",
                                                )
                                              : "",
                                          }))
                                          .filter((row) => hasText(row.value));
                                      }

                                      const doseMap = asObject(
                                        values?.doseProvidedTime,
                                      );
                                      return Object.keys(doseMap)
                                        .filter((doseKey) =>
                                          Boolean(doseMap[doseKey]),
                                        )
                                        .map((doseKey) => ({
                                          value: doseKey,
                                          time: srv?.dateTime
                                            ? moment(srv.dateTime).format(
                                                "h:mm A",
                                              )
                                            : "",
                                        }));
                                    })();

                                    const isPainScale =
                                      serviceNameKey === "pain scale";
                                    const isAdditionalService =
                                      serviceNameKey === "additional service";
                                    const isVitalSigns =
                                      serviceNameKey === "vital signs";
                                    const isBackCare =
                                      serviceNameKey === "back care";
                                    const isNutrition =
                                      serviceNameKey === "nutrition";
                                    const isSuppository =
                                      serviceNameKey === "suppository";
                                    const isWoundCare =
                                      serviceNameKey === "wound care";
                                    const isContinuousRyles =
                                      serviceNameKey ===
                                      "continuous ryle's tube aspiration";
                                    const isHourlyRyles =
                                      serviceNameKey ===
                                      "hourly ryle's tube aspiration";
                                    const isMedicationManagement =
                                      serviceNameKey ===
                                      "medication management";
                                    const nutritionCheckedText =
                                      checklistValues.length > 0
                                        ? checklistValues
                                            .map((item) => `checked ${item}`)
                                            .join("\n")
                                        : typeof rawValues === "string" ||
                                            typeof rawValues === "number"
                                          ? `checked ${String(rawValues).trim()}`
                                          : "";

                                    const hasAny =
                                      (isPainScale && hasText(painScaleText)) ||
                                      (isPhysiotherapist &&
                                        !isAdditionalService &&
                                        !isPainScale &&
                                        (hasText(servicesProvided) ||
                                          hasText(improvements))) ||
                                      (!isPhysiotherapist &&
                                        checklistValues.length > 0 &&
                                        !SERVICE_NAME_EXCLUDE_CHECKLIST.has(
                                          serviceNameKey,
                                        )) ||
                                      (!isPhysiotherapist &&
                                        isVitalSigns &&
                                        (hasText(values?.temperature) ||
                                          hasText(values?.Spo2) ||
                                          hasText(values?.bloodPressure) ||
                                          hasText(values?.bloodGlucose) ||
                                          hasText(values?.pulseRate) ||
                                          hasText(values?.respiratoryRate))) ||
                                      (!isPhysiotherapist &&
                                        isBackCare &&
                                        (hasText(values?.site) ||
                                          hasText(values?.grade) ||
                                          hasText(values?.action))) ||
                                      (!isPhysiotherapist &&
                                        isNutrition &&
                                        hasText(nutritionCheckedText)) ||
                                      (!isPhysiotherapist &&
                                        isSuppository &&
                                        (hasText(values?.type) ||
                                          hasText(values?.medication) ||
                                          hasText(values?.dosage) ||
                                          hasText(note))) ||
                                      (!isPhysiotherapist &&
                                        isWoundCare &&
                                        (hasText(values?.location) ||
                                          hasText(values?.type) ||
                                          hasText(values?.length) ||
                                          hasText(values?.width) ||
                                          hasText(values?.depth) ||
                                          hasText(values?.drainageAmount) ||
                                          hasText(values?.drainageType) ||
                                          hasText(note))) ||
                                      (!isPhysiotherapist &&
                                        (isContinuousRyles || isHourlyRyles) &&
                                        (hasText(values?.rate) ||
                                          hasText(values?.tube) ||
                                          hasText(values?.formula) ||
                                          hasText(values?.feedingType) ||
                                          hasText(note))) ||
                                      (isAdditionalService &&
                                        (hasText(additionalServiceName) ||
                                          hasText(additionalProvidedDetail) ||
                                          hasText(note))) ||
                                      (!isPhysiotherapist &&
                                        isMedicationManagement &&
                                        (hasText(values?.type) ||
                                          hasText(values?.name) ||
                                          hasText(values?.dose) ||
                                          hasText(values?.doseTime) ||
                                          hasText(values?.mealTime) ||
                                          medicationDoseRows.length > 0)) ||
                                      (!isPhysiotherapist &&
                                        !isPainScale &&
                                        !isVitalSigns &&
                                        !isBackCare &&
                                        !isNutrition &&
                                        !isSuppository &&
                                        !isWoundCare &&
                                        !isContinuousRyles &&
                                        !isHourlyRyles &&
                                        !isAdditionalService &&
                                        !isMedicationManagement &&
                                        (hasText(servicesProvided) ||
                                          hasText(improvements) ||
                                          hasText(serviceProvidedDetails) ||
                                          hasText(note) ||
                                          hasText(explicitServiceName)));

                                    const renderBlock = (label, val) => (
                                      <div>
                                        <div className="text-[13px] font-semibold font-poppins text-gray-900">
                                          {label !== "Pain Scale" ? label : ""}
                                        </div>
                                        <div className="text-[12px] font-poppins text-gray-600 mt-1 whitespace-pre-line break-words">
                                          {formatValue(val)}
                                        </div>
                                      </div>
                                    );

                                    return (
                                      <div key={id} className="relative pl-12">
                                        <div className="absolute left-[10px] top-[6px]">
                                          <div className="h-5 w-5 rounded-full bg-white border-[4px] border-emerald-500" />
                                        </div>

                                        <div className="text-[13px] font-semibold font-poppins text-[#3E57E8]">
                                          {timeOnly}
                                        </div>

                                        <div
                                          className="mt-2 text-[15px] font-semibold font-poppins text-[#3E57E8] break-words"
                                          title={String(title || "")}
                                        >
                                          {formatValue(title)}
                                        </div>

                                        <div className="mt-4 space-y-5">
                                          {!hasAny ? (
                                            <div className="text-[11px] text-gray-500 font-poppins">
                                              No values recorded.
                                            </div>
                                          ) : (
                                            <>
                                              {isPainScale
                                                ? renderBlock(
                                                    "Pain Scale",
                                                    painScaleText,
                                                  )
                                                : null}

                                              {isPhysiotherapist &&
                                              !isAdditionalService &&
                                              !isPainScale ? (
                                                <>
                                                  {hasText(servicesProvided)
                                                    ? renderBlock(
                                                        "Services Provided",
                                                        servicesProvided,
                                                      )
                                                    : null}
                                                  {hasText(improvements)
                                                    ? renderBlock(
                                                        "Improvements",
                                                        improvements,
                                                      )
                                                    : null}
                                                </>
                                              ) : null}

                                              {!isPhysiotherapist &&
                                              checklistValues.length > 0 &&
                                              !SERVICE_NAME_EXCLUDE_CHECKLIST.has(
                                                serviceNameKey,
                                              ) ? (
                                                <div className="space-y-1">
                                                  {checklistValues.map(
                                                    (item, idx2) => (
                                                      <div
                                                        key={`${id}-checked-${idx2}`}
                                                        className="text-[12px] font-poppins text-gray-600 mt-1 whitespace-pre-line break-words"
                                                      >
                                                        checked {item}
                                                      </div>
                                                    ),
                                                  )}
                                                </div>
                                              ) : null}

                                              {!isPhysiotherapist &&
                                              isVitalSigns ? (
                                                <>
                                                  {hasText(values?.temperature)
                                                    ? renderBlock(
                                                        "Temperature (°F)",
                                                        values.temperature,
                                                      )
                                                    : null}
                                                  {hasText(values?.Spo2)
                                                    ? renderBlock(
                                                        "SpO2",
                                                        values.Spo2,
                                                      )
                                                    : null}
                                                  {hasText(
                                                    values?.bloodPressure,
                                                  )
                                                    ? renderBlock(
                                                        "Blood Pressure (mmHg)",
                                                        values.bloodPressure,
                                                      )
                                                    : null}
                                                  {hasText(values?.bloodGlucose)
                                                    ? renderBlock(
                                                        "Blood Glucose (mg/dL)",
                                                        values.bloodGlucose,
                                                      )
                                                    : null}
                                                  {hasText(values?.pulseRate)
                                                    ? renderBlock(
                                                        "Pulse (bpm)",
                                                        values.pulseRate,
                                                      )
                                                    : null}
                                                  {hasText(
                                                    values?.respiratoryRate,
                                                  )
                                                    ? renderBlock(
                                                        "Respiratory Rate",
                                                        values.respiratoryRate,
                                                      )
                                                    : null}
                                                </>
                                              ) : null}

                                              {!isPhysiotherapist &&
                                              isBackCare ? (
                                                <>
                                                  {hasText(values?.site)
                                                    ? renderBlock(
                                                        "Site",
                                                        values.site,
                                                      )
                                                    : null}
                                                  {hasText(values?.grade)
                                                    ? renderBlock(
                                                        "Grade",
                                                        values.grade,
                                                      )
                                                    : null}
                                                  {hasText(values?.action)
                                                    ? renderBlock(
                                                        "Action Taken",
                                                        values.action,
                                                      )
                                                    : null}
                                                </>
                                              ) : null}

                                              {!isPhysiotherapist && isNutrition
                                                ? renderBlock(
                                                    "Nutrition",
                                                    nutritionCheckedText,
                                                  )
                                                : null}

                                              {!isPhysiotherapist &&
                                              isSuppository ? (
                                                <>
                                                  {hasText(values?.type)
                                                    ? renderBlock(
                                                        "Type of Suppository",
                                                        values.type,
                                                      )
                                                    : null}
                                                  {hasText(values?.medication)
                                                    ? renderBlock(
                                                        "Medication Name",
                                                        values.medication,
                                                      )
                                                    : null}
                                                  {hasText(values?.dosage)
                                                    ? renderBlock(
                                                        "Dosage (mg/IU)",
                                                        values.dosage,
                                                      )
                                                    : null}
                                                  {hasText(note)
                                                    ? renderBlock("Note", note)
                                                    : null}
                                                </>
                                              ) : null}

                                              {!isPhysiotherapist &&
                                              isWoundCare ? (
                                                <>
                                                  {hasText(values?.location)
                                                    ? renderBlock(
                                                        "Wound Location",
                                                        values.location,
                                                      )
                                                    : null}
                                                  {hasText(values?.type)
                                                    ? renderBlock(
                                                        "Wound Type",
                                                        values.type,
                                                      )
                                                    : null}
                                                  {hasText(values?.length) ||
                                                  hasText(values?.width) ||
                                                  hasText(values?.depth)
                                                    ? renderBlock(
                                                        "Wound Size",
                                                        `Length: ${values?.length || "-"} cm\nWidth: ${values?.width || "-"} cm\nDepth: ${values?.depth || "-"} cm`,
                                                      )
                                                    : null}
                                                  {hasText(
                                                    values?.drainageAmount,
                                                  )
                                                    ? renderBlock(
                                                        "Drainage Amount",
                                                        values.drainageAmount,
                                                      )
                                                    : null}
                                                  {hasText(values?.drainageType)
                                                    ? renderBlock(
                                                        "Drainage Type",
                                                        values.drainageType,
                                                      )
                                                    : null}
                                                  {hasText(note)
                                                    ? renderBlock("Note", note)
                                                    : null}
                                                </>
                                              ) : null}

                                              {!isPhysiotherapist &&
                                              (isContinuousRyles ||
                                                isHourlyRyles) ? (
                                                <>
                                                  {hasText(values?.rate)
                                                    ? renderBlock(
                                                        "Rate (mL/Hr)",
                                                        values.rate,
                                                      )
                                                    : null}
                                                  {hasText(values?.tube)
                                                    ? renderBlock(
                                                        "Tube",
                                                        values.tube,
                                                      )
                                                    : null}
                                                  {hasText(values?.formula)
                                                    ? renderBlock(
                                                        "Formula",
                                                        values.formula,
                                                      )
                                                    : null}
                                                  {hasText(values?.feedingType)
                                                    ? renderBlock(
                                                        "Feeding Type",
                                                        values.feedingType,
                                                      )
                                                    : null}
                                                  {hasText(note)
                                                    ? renderBlock("Note", note)
                                                    : null}
                                                </>
                                              ) : null}

                                              {isAdditionalService ? (
                                                <>
                                                  {hasText(
                                                    additionalServiceName,
                                                  )
                                                    ? renderBlock(
                                                        "Service Name",
                                                        additionalServiceName,
                                                      )
                                                    : null}
                                                  {hasText(
                                                    additionalProvidedDetail,
                                                  )
                                                    ? renderBlock(
                                                        "Service Provided Details",
                                                        additionalProvidedDetail,
                                                      )
                                                    : null}
                                                  {hasText(note)
                                                    ? renderBlock("Note", note)
                                                    : null}
                                                </>
                                              ) : null}

                                              {!isPhysiotherapist &&
                                              isMedicationManagement ? (
                                                <>
                                                  {hasText(values?.type)
                                                    ? renderBlock(
                                                        "Drug Type",
                                                        values.type,
                                                      )
                                                    : null}
                                                  {hasText(values?.name)
                                                    ? renderBlock(
                                                        "Drug Name",
                                                        values.name,
                                                      )
                                                    : null}
                                                  {hasText(values?.dose)
                                                    ? renderBlock(
                                                        "Dose",
                                                        values.dose,
                                                      )
                                                    : null}
                                                  {hasText(values?.doseTime)
                                                    ? renderBlock(
                                                        "Dose Timing",
                                                        values.doseTime,
                                                      )
                                                    : null}
                                                  {hasText(values?.mealTime)
                                                    ? renderBlock(
                                                        "Meal Time",
                                                        values.mealTime,
                                                      )
                                                    : null}
                                                  {medicationDoseRows.length > 0
                                                    ? renderBlock(
                                                        "Time Provided",
                                                        medicationDoseRows
                                                          .map((row) =>
                                                            row.time
                                                              ? `${row.value} - ${row.time}`
                                                              : row.value,
                                                          )
                                                          .join("\n"),
                                                      )
                                                    : null}
                                                </>
                                              ) : null}

                                              {!isPhysiotherapist &&
                                              !isPainScale &&
                                              !isVitalSigns &&
                                              !isBackCare &&
                                              !isNutrition &&
                                              !isSuppository &&
                                              !isWoundCare &&
                                              !isContinuousRyles &&
                                              !isHourlyRyles &&
                                              !isAdditionalService &&
                                              !isMedicationManagement &&
                                              !SERVICE_NAME_EXCLUDE_CHECKLIST.has(
                                                serviceNameKey,
                                              ) &&
                                              checklistValues.length === 0 ? (
                                                <>
                                                  {hasText(explicitServiceName)
                                                    ? renderBlock(
                                                        "Service Name",
                                                        explicitServiceName,
                                                      )
                                                    : null}
                                                  {hasText(servicesProvided)
                                                    ? renderBlock(
                                                        "Services Provided",
                                                        servicesProvided,
                                                      )
                                                    : null}
                                                  {hasText(improvements)
                                                    ? renderBlock(
                                                        "Improvements",
                                                        improvements,
                                                      )
                                                    : null}
                                                  {hasText(
                                                    serviceProvidedDetails,
                                                  )
                                                    ? renderBlock(
                                                        "Service Provided Details",
                                                        serviceProvidedDetails,
                                                      )
                                                    : null}
                                                  {hasText(note)
                                                    ? renderBlock("Note", note)
                                                    : null}
                                                </>
                                              ) : null}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t bg-white flex justify-end">
              <button
                onClick={closeAllReportsModal}
                className="px-5 py-2 rounded-xl bg-[#3E57E8] text-white text-[12px] font-semibold font-poppins hover:bg-[#3249d6] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showTracking && trackingData && (
        <LiveTrackingMap
          visible={showTracking}
          onClose={() => setShowTracking(false)}
          visitID={trackingData.visitID}
          dutyID={trackingData.dutyID}
          patientLocation={trackingData.patientLocation}
          initialHCPLocation={trackingData.initialHCPLocation}
          hcpName={trackingData.hcpName}
          hcpRole={trackingData.hcpRole}
        />
      )}

      {labFailureTarget && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lab-failure-title"
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h3
                  id="lab-failure-title"
                  className="text-[15px] font-semibold font-poppins text-gray-900"
                >
                  Close Lab Collection as Failed
                </h3>
                <p className="mt-1 text-[11px] font-poppins text-gray-500">
                  The patient and assigned collector will be notified.
                </p>
              </div>
              <button
                type="button"
                onClick={closeLabFailureModal}
                disabled={labFailureSaving}
                className="rounded-full p-1.5 hover:bg-gray-100 disabled:opacity-50"
                aria-label="Close failure dialog"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4">
              <label
                htmlFor="lab-failure-reason"
                className="text-[12px] font-semibold font-poppins text-gray-800"
              >
                Failure reason
              </label>
              <textarea
                id="lab-failure-reason"
                value={labFailureReason}
                onChange={(event) => setLabFailureReason(event.target.value)}
                maxLength={1000}
                rows={5}
                disabled={labFailureSaving}
                placeholder="Explain why the sample collection could not be completed"
                className="mt-2 w-full resize-y rounded-xl border border-gray-300 px-3 py-2 text-[12px] font-poppins outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 disabled:bg-gray-100"
              />
              <div className="mt-1 text-right text-[10px] text-gray-500">
                {labFailureReason.length}/1000
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={closeLabFailureModal}
                disabled={labFailureSaving}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-[12px] font-semibold font-poppins text-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitLabCollectionFailure}
                disabled={labFailureSaving || !labFailureReason.trim()}
                className="rounded-lg bg-red-600 px-4 py-2 text-[12px] font-semibold font-poppins text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {labFailureSaving ? "Closing..." : "Confirm Failure"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DUTY CLOSE MODAL */}
      {dutyCloseModalOpen && isReadOnly && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
              <h3 className="text-[16px] font-semibold font-poppins text-gray-900">
                Close Duty{" "}
              </h3>
              <button
                onClick={closeDutyCloseModal}
                className="p-1.5 rounded-full hover:bg-blue-100 transition-colors"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </div>
            <div className="px-6 py-6">
              <p className="text-[13px] font-poppins text-gray-700 mb-4 font-medium text-center">
                Close Duty with:
              </p>
              <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3 w-full">
                <button
                  onClick={() => setDutyCloseType("noPayout")}
                  className={`flex-1 flex items-center justify-center text-center min-h-[60px] py-2 px-3 rounded-md text-[12px] font-poppins border transition-all ${
                    dutyCloseType === "noPayout"
                      ? "bg-white text-[#4951EC] border-[#4951EC] ring-1 ring-[#4951EC] font-semibold"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                  }`}
                >
                  Without payout
                </button>
                <button
                  onClick={() => setDutyCloseType("payoutForAll")}
                  className={`flex-1 flex items-center justify-center text-center min-h-[60px] py-2 px-3 rounded-md text-[12px] font-poppins border transition-all ${
                    dutyCloseType === "payoutForAll"
                      ? "bg-white text-[#4951EC] border-[#4951EC] ring-1 ring-[#4951EC] font-semibold"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                  }`}
                >
                  Payout for both Partner & Ceviliyar
                </button>
                <button
                  onClick={() => setDutyCloseType("payoutForCeviliyar")}
                  className={`flex-1 flex items-center justify-center text-center min-h-[60px] py-2 px-3 rounded-md text-[12px] font-poppins border transition-all ${
                    dutyCloseType === "payoutForCeviliyar"
                      ? "bg-white text-[#4951EC] border-[#4951EC] ring-1 ring-[#4951EC] font-semibold"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                  }`}
                >
                  Payout for Ceviliyar
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-center">
              <button
                onClick={submitDutyClose}
                disabled={!dutyCloseType}
                className="px-8 py-2 rounded-lg text-[13px] font-poppins font-medium text-white bg-[#4951EC] hover:bg-[#3d3acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Visits;
